import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Reorder } from 'motion/react';
import { doc, setDoc, addDoc, collection, writeBatch } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { cn, calculateOVR, deepClean, getDefensiveSignal } from '../lib/utils';
import { track } from '../lib/analytics';
import { generateAutoLineup } from '../lib/lineupUtils';
import { analyzeFairness, anyFairnessRuleOn, FairnessFinding } from '../lib/fairness-check';
import { PITCH_COUNT_RULESETS, calculateRestDays, getPitcherEligibility, buildCustomTournamentRules } from '../lib/pitch-count-rules';
import { X, Shield, Zap, Activity, ChevronRight, Check, Save, Upload, AlertTriangle, MessageSquare, Wand2, RefreshCw, FileUp, ClipboardList, ChevronUp, ChevronDown, Users, Brain, UserPlus, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { useClockRemaining, clockRemainingMs } from '../lib/game-clock';
import { startGameActivity, updateGameActivity, endGameActivity } from '../lib/game-clock-live-activity';
import { buildPagerState } from '../lib/pocket-pager';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { tapLight } from '../lib/haptics';
import { DiamondView } from './DiamondView';
import { EndGameModal } from './LiveGameEngine/EndGameModal';
import { InningFlowModal } from './LiveGameEngine/InningFlowModal';
import { BenchAndBullpen } from './LiveGameEngine/BenchAndBullpen';
import { FillEmptyPositionModal } from './LiveGameEngine/FillEmptyPositionModal';
import { PitchingIntelPanel } from './PitchingIntelPanel';
import { TournamentCelebration } from './TournamentCelebration';
import { BetweenGamesAdvisor } from './BetweenGamesAdvisor';
import { PitchingTrackerPanel } from './LiveGameEngine/PitchingTrackerPanel';
import { LiveLineupWall } from './LiveGameEngine/LiveLineupWall';
import { SidePane, type SideView } from './LiveGameEngine/SidePane';
import { useIsWide } from '../hooks/useIsWide';
import { SyncNote } from './SyncNote';
import { saveLocalFirst } from '../lib/save-local-first';
import { logsWithInningInProgress } from '../lib/game-positions';
import Papa from 'papaparse';

// Auto Lineup fill modes. `smart` is the default: it runs the engine using
// whatever Fairness rules are on in Settings, so its actual behavior can differ
// team to team - that's intentional, not a bug. The other three OVERRIDE those
// settings for one tap: Sub In Bench does a narrow swap (bench in for whoever's
// played the most), New Positions Every Inning and Best Defenders push fairness
// to its two extremes (always rotate vs. ignore rotation entirely). `buttonLabel`
// is the short form shown on the collapsed button; `label`/`description` are the
// dropdown row. Names match DEFENSE_MODES in lib/batting-order.ts (kept in sync
// by hand) so the Game Planner and live game use identical language. Two prior
// modes (a pure-OVR infield stack and a random shuffle) were removed
// 2026-09-10 as confusing and rarely useful - a coach who wants either can set
// positions by hand. Renamed again 2026-09-10 per Dan: Smart/Fair Rotation ->
// Equal Bench Time, Force Rotation -> New Positions Every Inning, Best Lineup ->
// Best Defenders.
const AUTO_LINEUP_MODES: { key: 'smart' | 'sub_bench' | 'rotate_all' | 'competitive'; buttonLabel: string; label: string; description: string }[] = [
  {
    key: 'smart',
    buttonLabel: 'Equal Bench Time',
    label: 'Equal Bench Time',
    description: 'Uses your Fairness settings',
  },
  {
    key: 'sub_bench',
    buttonLabel: 'Sub In Bench',
    label: 'Sub In Bench',
    description: "Swaps bench in for the players who've played the most",
  },
  {
    key: 'rotate_all',
    buttonLabel: 'New Positions Every Inning',
    label: 'New Positions Every Inning',
    description: 'No one repeats a position from last inning',
  },
  {
    key: 'competitive',
    buttonLabel: 'Best Defenders',
    label: 'Best Defenders',
    description: 'Strongest fielders win, fairness rules ignored',
  },
];

const LiveGameEngine = ({ game, team, players, teamId, games, tournaments = [], onClose, forceEndStep, isHeadCoach = true, isElite = false, onUpgrade, isPracticeMode = false, hasUnsyncedChanges = false }: { game: any, team: any, players: any[], teamId: string, games: any[], tournaments?: any[], onClose: () => void, forceEndStep?: 'score' | 'pitch_counts', isHeadCoach?: boolean, isElite?: boolean, onUpgrade?: () => void, isPracticeMode?: boolean, hasUnsyncedChanges?: boolean }) => {
  const [lineup, setLineup] = useState<any[]>(game.lineup || []);
  const [pitchCounts, setPitchCounts] = useState<Record<string, number>>(game.pitchCounts || {});
  const [isPitchingIntelOpen, setIsPitchingIntelOpen] = useState(false);
  const [isGeneratingLineup, setIsGeneratingLineup] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [emptyPositionToFill, setEmptyPositionToFill] = useState<string | null>(null);
  const [inningLogs, setInningLogs] = useState<Record<number, { id: string, position: string }[]>>(() => {
    const logs = game.inningLogs || {};
    if (Array.isArray(logs)) {
      const logsObj: Record<number, any> = {};
      logs.forEach((log, idx) => {
        if (log) logsObj[idx] = log;
      });
      return logsObj;
    }
    return logs;
  });
  const [lastBatterId, setLastBatterId] = useState<string | null>(game.lastBatterId || null);
  const [pitchedThisGame, setPitchedThisGame] = useState<string[]>(game.pitchedThisGame || (game.lineup?.find((p: any) => p.position === 'P') ? [game.lineup.find((p: any) => p.position === 'P').id] : []));

  // Inning Flow State
  const [currentInning, setCurrentInning] = useState(game.currentInning || 1);
  const [inningFlowStep, setInningFlowStep] = useState<'log_pitches' | 'select_pitcher' | 'next_pitcher' | 'fairness_review' | 'set_defense' | null>(null);
  const [changePitcherStep, setChangePitcherStep] = useState<'log_pitches' | 'select_pitcher' | null>(null);
  const [changePitcherInningPitches, setChangePitcherInningPitches] = useState('');
  const [changePitcherOuts, setChangePitcherOuts] = useState('0');
  const [showMorePitchers, setShowMorePitchers] = useState(false);
  const [inningPitchLogs, setInningPitchLogs] = useState<{pitcherId: string, pitches: number}[]>([]);
  const [currentLoggingPitcherId, setCurrentLoggingPitcherId] = useState<string | null>(null);
  const [inningPitches, setInningPitches] = useState('');
  const [willPitcherContinue, setWillPitcherContinue] = useState<boolean | null>(null);
  const [isBullpenOpen, setIsBullpenOpen] = useState(false);

  // End Game Flow State
  const [endGameStep, setEndGameStep] = useState<'score' | 'pitch_counts' | 'game_ball' | null>(forceEndStep || null);
  const [selectedGameBallIds, setSelectedGameBallIds] = useState<string[]>([]);
  const [awayScore, setAwayScore] = useState('');
  const [homeScore, setHomeScore] = useState('');
  const [finalPitchCounts, setFinalPitchCounts] = useState<Record<string, number>>(game.pitchCounts || {});

  // Game clock (opt-in countdown for timed games). The clock is an "ends at"
  // timestamp persisted on the game doc, but the live readout is driven by LOCAL
  // state so start/clear/restart respond instantly — the `game` prop is a snapshot
  // that doesn't necessarily re-sync after we write. The effect keeps us in step
  // if the game doc's clock is changed elsewhere (e.g. another coach's device).
  const [clockEndsAt, setClockEndsAt] = useState<string | null>(game.clockEndsAt ?? null);
  useEffect(() => { setClockEndsAt(game.clockEndsAt ?? null); }, [game.clockEndsAt]);
  const clock = useClockRemaining(clockEndsAt);
  const [clockModal, setClockModal] = useState(false);
  const [clockMin, setClockMin] = useState(String(team.settings?.defaultGameClockMinutes ?? 75));
  const writeGameClock = async (endsAt: string | null, durationMin?: number) => {
    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id),
        { clockEndsAt: endsAt, ...(durationMin != null ? { clockDurationMin: durationMin } : {}) },
        { merge: true });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `teams/${teamId}/games/${game.id}`);
    }
  };
  const startGameClock = async (minutes: number) => {
    if (!minutes || minutes <= 0) return;
    const endsAt = new Date(Date.now() + minutes * 60000).toISOString();
    // The Lock Screen activity picks up clockEndsAt from local state (Pocket
    // Pager effect below), so it appears without waiting on this write, which
    // only settles once the server acks it.
    setClockEndsAt(endsAt);
    await writeGameClock(endsAt, minutes);
  };
  const clearGameClock = async () => {
    setClockEndsAt(null);
    await writeGameClock(null);
  };
  // Warn (red) in the final 2 minutes or once expired.
  const clockUrgent = clock.isExpired || (clock.isRunning && clockRemainingMs(clockEndsAt) < 120000);
  const [isFinishingGame, setIsFinishingGame] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showAdvisor, setShowAdvisor] = useState(false);

  const [activeTab, setActiveTab] = useState<'defense' | 'pitch_tracker' | 'lineup'>('defense');
  // The at-bat in progress, held here (not in PitchingTrackerPanel) so a tab
  // switch or a fold/unfold that rearranges the layout can't drop it. An
  // at-bat never spans innings, so a new inning starts it empty, which is what
  // unmounting the panel used to do as a side effect.
  const [trackerPitches, setTrackerPitches] = useState<('B' | 'S')[]>([]);
  useEffect(() => { setTrackerPitches([]); }, [currentInning]);

  // Now | Next (wide screens): which view the right pane shows, remembered per
  // team on this device. A preference, so localStorage, guarded.
  const isWide = useIsWide();
  const sideViewKey = `dugout.sideView.${teamId}`;
  const [sideView, setSideViewState] = useState<SideView>(() => {
    try {
      const v = localStorage.getItem(sideViewKey);
      if (v === 'next' || v === 'pitches' || v === 'fieldtime') return v;
    } catch { /* storage unavailable */ }
    return 'next';
  });
  const setSideView = (v: SideView) => {
    setSideViewState(v);
    try { localStorage.setItem(sideViewKey, v); } catch { /* storage unavailable */ }
    // One tracker on screen at a time: the right pane takes it from the left.
    if (v === 'pitches' && activeTab === 'pitch_tracker') setActiveTab('defense');
  };
  // ...and the left pane's Pitches tab takes it back from the right.
  useEffect(() => {
    if (isWide && activeTab === 'pitch_tracker' && sideView === 'pitches') setSideViewState('next');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isWide]);
  const [pitchSequence, setPitchSequence] = useState<any[]>(game.pitchSequence || []);
  // Catcher development events (passed balls / blocks) — auto-attributed to the
  // player at position 'C' when the event is logged. Persisted on the game doc
  // alongside pitchSequence so they survive a mid-game reload.
  const [catcherEvents, setCatcherEvents] = useState<any[]>(game.catcherEvents || []);

  type AutoLineupMode = (typeof AUTO_LINEUP_MODES)[number]['key'];
  const [autoLineupMode, setAutoLineupMode] = useState<AutoLineupMode>('smart');
  const [isAutoLineupOpen, setIsAutoLineupOpen] = useState(false);
  const [gameResult, setGameResult] = useState<any>(null);
  const [alertModal, setAlertModal] = useState<{ title: string; message: string; buttonLabel?: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; confirmLabel: string; cancelLabel?: string; onConfirm: () => void } | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [finishGameError, setFinishGameError] = useState<string | null>(null);
  const [prevPitcherState, setPrevPitcherState] = useState<{
    lineup: any[];
    pitchCounts: Record<string, number>;
    pitchedThisGame: string[];
  } | null>(null);
  const [isLatePlayerModalOpen, setIsLatePlayerModalOpen] = useState(false);
  // Add / Remove Player panel (game menu). One list of the whole roster, each row
  // toggling between "in today's game" and "out". Replaces the old bench "..."
  // action sheet, which was reachable only from the bench and offered a redundant
  // "send to bench" for players already sitting.
  const [isRosterManagerOpen, setIsRosterManagerOpen] = useState(false);
  const [lateArrivals, setLateArrivals] = useState<string[]>(game.lateArrivals || []);
  // Players pulled from today's game (injury, left early). Kept so fairness can
  // stop counting innings they were never available for.
  const [removedPlayerIds, setRemovedPlayerIds] = useState<string[]>(game.removedPlayerIds || []);

  // Sync finalPitchCounts if forceEndStep is provided
  useEffect(() => {
    if (forceEndStep === 'score') {
      setFinalPitchCounts(game.pitchCounts || {});
    }
  }, [forceEndStep, game.pitchCounts]);

  // Pre-populate inning pitch count from tracker when end-inning log_pitches step opens
  useEffect(() => {
    if (inningFlowStep === 'log_pitches' && currentLoggingPitcherId) {
      const count = pitchSequence
        .filter((ab: any) => ab.pitcherId === currentLoggingPitcherId && ab.inning === currentInning && ab.outcome !== null)
        .reduce((sum: number, ab: any) => sum + (ab.pitches?.length ?? 0), 0);
      if (count > 0) setInningPitches(String(count));
    }
  }, [inningFlowStep, currentLoggingPitcherId]);

  // Pre-populate inning pitch count from tracker when change-pitcher log_pitches step opens
  useEffect(() => {
    if (changePitcherStep === 'log_pitches' && currentPitcher) {
      const count = pitchSequence
        .filter((ab: any) => ab.pitcherId === currentPitcher.id && ab.inning === currentInning && ab.outcome !== null)
        .reduce((sum: number, ab: any) => sum + (ab.pitches?.length ?? 0), 0);
      if (count > 0) setChangePitcherInningPitches(String(count));
    }
  }, [changePitcherStep]);

  // Auto-switch to Defense tab when set_defense step begins
  useEffect(() => {
    if (inningFlowStep === 'set_defense') {
      setActiveTab('defense');
    }
  }, [inningFlowStep]);

  const currentPitcher = lineup.find((p: any) => p.position === 'P');
  const currentCatcher = lineup.find((p: any) => p.position === 'C');
  // Include pitches logged in the inning-end flow that haven't been flushed
  // into pitchCounts yet — so the coach sees true running totals during the
  // between-innings "set_defense" view, not stale counts.
  const pendingInningPitchesFor = (id: string) =>
    inningPitchLogs.filter(l => l.pitcherId === id).reduce((s, l) => s + l.pitches, 0);
  // Also count pitches logged via the pitching tracker panel (pitchSequence),
  // which doesn't write to pitchCounts until end of game.
  const pitchTrackerCountFor = (id: string) =>
    pitchSequence
      .filter((ab: any) => ab.pitcherId === id && ab.outcome !== null)
      .reduce((sum: number, ab: any) => sum + (ab.pitches?.length ?? 0), 0);
  const currentPitchCount = currentPitcher
    ? Math.max(
        (pitchCounts[currentPitcher.id] || 0) + pendingInningPitchesFor(currentPitcher.id),
        pitchTrackerCountFor(currentPitcher.id)
      )
    : 0;

  // Live per-pitcher counts that fold in mid-inning pitches not yet flushed to
  // `pitchCounts` — pending inning-end logs and the live pitch-tracker sequence.
  // The Pitching Intel panel reads counts off this so it stays in sync DURING an
  // inning, not only after the inning is locked. Uses the same max() reconciliation
  // as currentPitchCount so the tracker and inning-end flow can't double-count.
  const livePitchCounts: Record<string, number> = (() => {
    const merged: Record<string, number> = { ...pitchCounts };
    const ids = new Set<string>([
      ...Object.keys(pitchCounts),
      ...inningPitchLogs.map(l => l.pitcherId),
      ...pitchSequence.map((ab: any) => ab.pitcherId).filter(Boolean),
    ]);
    ids.forEach(id => {
      merged[id] = Math.max(
        (pitchCounts[id] || 0) + pendingInningPitchesFor(id),
        pitchTrackerCountFor(id),
      );
    });
    return merged;
  })();

  const tournamentRulesetOverride = (() => {
    if (!game.tournamentId) return undefined;
    const t = tournaments.find((t: any) => t.id === game.tournamentId);
    return t?.pitchCountRulesetOverride
      || team.settings?.tournamentOrg
      || team.settings?.pitchCountRuleset
      || 'little_league';
  })();

  const tournamentCustomRules = (() => {
    const isCustom = !!tournamentRulesetOverride && PITCH_COUNT_RULESETS[tournamentRulesetOverride]?.id === 'custom';
    const t = game.tournamentId ? tournaments.find((t: any) => t.id === game.tournamentId) : undefined;
    return buildCustomTournamentRules(isCustom, t, team.settings);
  })();

  // Single source of truth for "is this pitcher on mandatory rest for today's
  // game?" — asks the rules engine LIVE (same as the Lineup Builder) instead of
  // reading a stored `eligibleDate` off the last log entry. The stored field is
  // absent on GameChanger-imported / demo logs, which is why the bullpen and
  // Change Pitcher screens used to show on-rest arms as available.
  const restRuleset = PITCH_COUNT_RULESETS[tournamentRulesetOverride || team.settings?.pitchCountRuleset || 'little_league']
    || PITCH_COUNT_RULESETS['little_league'];
  const restAgeGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
  const restGameDate = (() => { const d = new Date(game.date); d.setHours(0, 0, 0, 0); return d; })();
  const checkPitcherRest = (playerId: string): { onRest: boolean; until: string | null } => {
    if (team.styleOfPlay !== 'playerspitch' || !restRuleset) return { onRest: false, until: null };
    const full = players.find((pl: any) => pl.id === playerId);
    const result = getPitcherEligibility(
      full?.pitchingLog || [],
      restRuleset,
      restAgeGroup,
      restGameDate,
      0,
      game.tournamentId,
      true,
      tournamentCustomRules,
    );
    if (result.eligible) return { onRest: false, until: null };
    const eligibleDate = result.restStatus?.eligibleDate;
    const until = eligibleDate
      ? new Date(eligibleDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : null;
    return { onRest: true, until };
  };

  // ── Live daily-limit warning for the current pitcher ───────────────────────
  // Some leagues enforce a HARD cap (no "finish the at-bat" grace), so the coach
  // needs a heads-up BEFORE the at-bat ends — pitches register live in the
  // tracker, but the cap is only checked on commit. We compute the daily cap and
  // how many this pitcher has thrown today across ALL of today's games, so the
  // warning reflects the true running daily total, not just this game.
  const currentPitcherDailyState = (() => {
    if (team.styleOfPlay !== 'playerspitch' || !currentPitcher || !restRuleset) return null;
    const elig = getPitcherEligibility(
      currentPitcher.pitchingLog || [], restRuleset, restAgeGroup, restGameDate,
      0, game.tournamentId, true, tournamentCustomRules,
    );
    const dailyMax = elig.dailyMax;
    if (!dailyMax || dailyMax <= 0) return null;
    // Pitches already thrown TODAY in prior games (same day, same tournament).
    const priorToday = games.reduce((sum: number, g: any) => {
      if (g.id === game.id) return sum;
      if (game.tournamentId && g.tournamentId !== game.tournamentId) return sum;
      if (new Date(g.date).toDateString() !== new Date(game.date).toDateString()) return sum;
      return sum + ((g.pitchCounts?.[currentPitcher.id] as number) || 0);
    }, 0);
    return { dailyMax, priorToday };
  })();

  const getPlayerAt = (pos: string) => lineup.find((p: any) => p.position === pos);
  const benchPlayers = lineup.filter((p: any) => p.position === 'BENCH' || p.position === 'EH');
  const absentPlayers = players.filter(p => !lineup.find((l: any) => l.id === p.id));
  const bullpenEligible: any[] = [];
  const bullpenUnavailable: { player: any; reason: string }[] = [];
  const isTournamentGame = !!game.tournamentId;
  lineup.forEach((p: any) => {
    const isPitcher = isTournamentGame
      ? true
      : p.positions?.['P'] >= 1 ||
        (p.attributes?.pitching?.velocity >= 1 || p.attributes?.pitching?.control >= 1);
    if (!isPitcher || p.position === 'P') return;

    if (team.settings?.excludeCatcherFromBullpen && p.position === 'C') {
      bullpenUnavailable.push({ player: p, reason: 'Playing catcher' });
    } else if (pitchedThisGame.includes(p.id)) {
      bullpenUnavailable.push({ player: p, reason: 'Already pitched' });
    } else {
      bullpenEligible.push(p);
    }
  });

  // Also surface roster pitchers who are absent/late but on mandatory rest —
  // so the coach sees Luke Stoppel as unavailable even if he's not in today's lineup.
  const lineupIds = new Set(lineup.map((p: any) => p.id));
  players.forEach((p: any) => {
    if (lineupIds.has(p.id)) return; // already handled above
    const isPitcher = isTournamentGame
      ? true
      : (p.positions?.['P'] ?? p.ratings?.P ?? 0) >= 1 ||
        (p.attributes?.pitching?.velocity >= 1 || p.attributes?.pitching?.control >= 1);
    if (!isPitcher) return;
    const rest = checkPitcherRest(p.id);
    if (rest.onRest) {
      bullpenUnavailable.push({ player: p, reason: rest.until ? `On rest until ${rest.until}` : 'On mandatory rest' });
    }
  });
  const bullpenPlayers = bullpenEligible;

  const bullpenAvailable = bullpenEligible.filter(p => !checkPitcherRest(p.id).onRest);
  const bullpenOnRest: { player: any; until: string }[] = bullpenEligible
    .map(p => ({ p, rest: checkPitcherRest(p.id) }))
    .filter(({ rest }) => rest.onRest)
    .map(({ p, rest }) => ({ player: p, until: rest.until || 'a future date' }));

  // ── The coach's bullpen plan, as suggestions ──────────────────────────────
  // Game Planner saves plan.pitcherPlan (starter first, then relievers) and
  // puts each inning's planned arm at P in plan.innings. Nothing here changes
  // pitchers on its own: these only decide what the prompts suggest and which
  // arm is pinned first. Games planned without a bullpen read back nothing.
  // Spelled out rather than reading isPlayersPitch, which is declared further
  // down this component and would not exist yet at this point in the render.
  const planSuggestsPitchers = team.styleOfPlay === 'playerspitch';
  const canSuggestArm = (id: string | null | undefined) =>
    !!id &&
    id !== currentPitcher?.id &&
    !pitchedThisGame.includes(id) &&
    lineup.some((p: any) => p.id === id) &&
    !checkPitcherRest(id).onRest;
  /** First planned arm who has not pitched yet: "slotted next in the bullpen". */
  const nextPlannedArm = planSuggestsPitchers
    ? lineup.find((p: any) => p.id === (game.plan?.pitcherPlan || []).find((id: string) => canSuggestArm(id))) ?? null
    : null;
  /** Who the plan has on the mound for the coming inning, when that is a change. */
  const plannedPitcherNextInningId = (game.plan?.innings?.[String(currentInning)] || [])
    .find((pp: any) => pp.position === 'P')?.id;
  const plannedPitcherNextInning = planSuggestsPitchers && canSuggestArm(plannedPitcherNextInningId)
    ? lineup.find((p: any) => p.id === plannedPitcherNextInningId) ?? null
    : null;
  /** Bullpen list for the Change Pitcher sheet, with the next planned arm pinned first. */
  const changePitcherArms = nextPlannedArm
    ? [nextPlannedArm, ...bullpenAvailable.filter((p: any) => p.id !== nextPlannedArm.id)]
    : bullpenAvailable;

  // ── Pocket Pager (Lock Screen / Dynamic Island) ───────────────────────────
  // A glanceable mirror of this screen for a coach whose phone is closed: inning,
  // pitcher, count vs limit, what changes next inning. Built only from values
  // this component already computes, so it can't disagree with the screen.
  // Display only: nothing on the Lock Screen writes game state.
  const pagerAgeRules = restRuleset?.id === 'custom' && tournamentCustomRules
    ? tournamentCustomRules
    : (restRuleset?.ageGroups?.[restAgeGroup] || restRuleset?.ageGroups?.['all'] || restRuleset?.ageGroups?.['12U'] || null);
  // USSSA counts any inning a pitcher threw in as a full inning, so the current
  // inning counts as soon as he's on the mound.
  const pagerInningsToday = (() => {
    if (!currentPitcher) return 0;
    const loggedHere = Object.entries(inningLogs).filter(([inn, log]) =>
      Number(inn) !== currentInning && (log || []).some((e: any) => e.id === currentPitcher.id && e.position === 'P')
    ).length;
    const earlierToday = (currentPitcher.pitchingLog || []).reduce((sum: number, e: any) =>
      e.gameId !== game.id && new Date(e.date).toDateString() === new Date(game.date).toDateString()
        ? sum + (e.inningsPitched ?? 0)
        : sum, 0);
    return earlierToday + loggedHere + 1;
  })();
  // plan.innings is 0-based (inning 1 at "0"), so the coming inning sits at currentInning.
  const pagerNextInningChanges = (() => {
    const next = game.plan?.innings?.[String(currentInning)];
    if (!Array.isArray(next) || next.length === 0) return null;
    return next.filter((pp: any) => {
      const now = lineup.find((p: any) => p.id === pp.id);
      return !!now && now.position !== pp.position;
    }).length;
  })();
  const pagerState = buildPagerState({
    styleOfPlay: team.styleOfPlay,
    inning: currentInning,
    pitcherName: currentPitcher?.name,
    pitchesToday: currentPitchCount + (currentPitcherDailyState?.priorToday ?? 0),
    inningsToday: pagerInningsToday,
    ageRules: pagerAgeRules,
    nextPitcherName: plannedPitcherNextInning?.name,
    nextInningChanges: pagerNextInningChanges,
  });
  const pagerKey = JSON.stringify({ ...pagerState, endsAt: clockEndsAt });
  const pagerLatest = React.useRef({ state: pagerState, endsAt: clockEndsAt });
  pagerLatest.current = { state: pagerState, endsAt: clockEndsAt };
  // Set by End Game and Discard so the unmount cleanup and a pending update
  // don't touch an activity that's already been taken down.
  const pagerEnded = React.useRef(false);

  // Start when the live screen opens on a game in progress (this also carries a
  // clock started at Play Ball). On Pause & Exit the real game's clock keeps
  // running, so leave just the countdown; with no clock, take it down.
  useEffect(() => {
    if (forceEndStep) return;
    const { state, endsAt } = pagerLatest.current;
    startGameActivity({ teamName: team.name, opponent: game.opponent || 'Opponent', isHome: !!game.isHome, endsAt, ...state });
    return () => {
      if (pagerEnded.current) return;
      const liveEndsAt = pagerLatest.current.endsAt;
      if (liveEndsAt && clockRemainingMs(liveEndsAt) > 0) updateGameActivity({ endsAt: liveEndsAt });
      else endGameActivity();
    };
  }, []);

  // Mirror changes, debounced: pitch taps come in bursts.
  useEffect(() => {
    if (forceEndStep || pagerEnded.current) return;
    const t = setTimeout(() => {
      if (pagerEnded.current) return;
      updateGameActivity({ ...pagerLatest.current.state, endsAt: pagerLatest.current.endsAt });
    }, 800);
    return () => clearTimeout(t);
  }, [pagerKey]);

  // Tapping the activity opens dugout://live/pitches: bring players-pitch
  // coaches to the Pitches tab. UI only, no game state.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || team.styleOfPlay !== 'playerspitch') return;
    let sub: { remove: () => void } | undefined;
    let cancelled = false;
    CapApp.addListener('appUrlOpen', ({ url }) => {
      if (!url?.startsWith('dugout://live/pitches')) return;
      if (isWide) setSideView('pitches');
      else setActiveTab('pitch_tracker');
    }).then(s => { if (cancelled) s.remove(); else sub = s; });
    return () => { cancelled = true; sub?.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team.styleOfPlay, isWide]);

  // "See More Players": everyone in the lineup who isn't already surfaced in the
  // bullpen (no P rating, no pitching attributes — so they were filtered out) and
  // isn't the player currently on the mound. This is the escape hatch for the live
  // situation where a coach needs to send an un-scouted kid to pitch. Selecting one
  // tags them as a pitcher (positions.P = 1) so they show up in the bullpen next time.
  const surfacedIds = new Set<string>([
    ...bullpenEligible.map((p: any) => p.id),
    ...bullpenUnavailable.map(({ player }) => player.id),
    ...bullpenOnRest.map(({ player }) => player.id),
  ]);
  const moreBullpenPlayers = lineup.filter((p: any) =>
    !surfacedIds.has(p.id) && p.position !== 'P'
  );

  const applySubBenchMode = (lp: any[], logs: Record<number, { id: string; position: string }[]>) => {
    const pitcher = lp.find((p: any) => p.isCurrentPitcher);
    const benchPlayers = lp.filter((p: any) => p.position === 'BENCH' && p.id !== pitcher?.id);
    if (benchPlayers.length === 0) return lp;
    const fieldInnings: Record<string, number> = {};
    Object.values(logs).forEach(entries => {
      entries.forEach(({ id, position }) => {
        if (position !== 'BENCH' && position !== 'EH') {
          fieldInnings[id] = (fieldInnings[id] ?? 0) + 1;
        }
      });
    });
    const fieldPlayers = lp
      .filter((p: any) => p.position !== 'BENCH' && p.position !== 'EH' && !p.isCurrentPitcher)
      .sort((a: any, b: any) => (fieldInnings[b.id] ?? 0) - (fieldInnings[a.id] ?? 0));
    const toSit = fieldPlayers.slice(0, benchPlayers.length);
    const vacatedPositions = toSit.map((p: any) => p.position);
    const available = [...benchPlayers];
    const assignments: { id: string; position: string }[] = [];

    // Sort positions by fewest eligible bench players first so scarce spots get filled before easy ones
    const sortedPositions = [...vacatedPositions].sort((a, b) => {
      const eligA = available.filter((p: any) => (p.positions?.[a] ?? 0) > 0).length;
      const eligB = available.filter((p: any) => (p.positions?.[b] ?? 0) > 0).length;
      return eligA - eligB;
    });

    for (const pos of sortedPositions) {
      if (available.length === 0) break;
      const bestIdx = available.reduce((best, p, i) =>
        (p.positions?.[pos] ?? 0) > (available[best].positions?.[pos] ?? 0) ? i : best, 0
      );
      assignments.push({ id: available[bestIdx].id, position: pos });
      available.splice(bestIdx, 1);
    }

    return lp.map((p: any) => {
      const match = assignments.find((a) => a.id === p.id);
      if (match) return { ...p, position: match.position };
      if (toSit.some((s: any) => s.id === p.id)) return { ...p, position: 'BENCH' };
      return p;
    });
  };

  // Hydrate each lineup entry against the full player record before handing it to
  // the engine. game.lineup (esp. for imported/seeded games) can lack positions/
  // pitchingLog — without this the engine sees positions['P'] = 0 for everyone and
  // wrongly reports "No eligible pitchers remain." The lineup's own position/state
  // wins; everything else (ratings, pitching log) comes from the roster.
  const hydrateLineupForEngine = () =>
    lineup.map((p: any) => {
      const full = players.find((pl: any) => pl.id === p.id);
      // The roster doc is the source of truth for ratings. A lineup entry can
      // carry an EMPTY positions/pitchingLog (players re-added mid-game are
      // built from a trimmed object), and `??` would keep that empty value and
      // silently strip the player's real ratings, making them ineligible
      // everywhere. Fall back whenever the lineup copy has nothing in it.
      const hasRatings = p.positions && Object.keys(p.positions).length > 0;
      const hasLog = Array.isArray(p.pitchingLog) && p.pitchingLog.length > 0;
      return {
        ...full,
        ...p,
        positions: hasRatings ? p.positions : (full?.positions ?? {}),
        pitchingLog: hasLog ? p.pitchingLog : (full?.pitchingLog ?? []),
        isCurrentPitcher: p.id === currentPitcher?.id,
      };
    });

  const handleAutoLineup = async (modeOverride?: AutoLineupMode) => {
    setIsGeneratingLineup(true);

    setTimeout(async () => {
      try {
        const lineupWithPitcher = hydrateLineupForEngine();

        const activeMode = modeOverride ?? autoLineupMode;
        let finalLineup: any[];
        switch (activeMode) {
          case 'competitive':
            finalLineup = generateAutoLineup(lineupWithPitcher, { ...team.settings, fieldTimeEqualizerEnabled: false }, inningLogs, game.date, game.tournamentId, team.styleOfPlay);
            break;
          case 'rotate_all':
            finalLineup = generateAutoLineup(lineupWithPitcher, { ...team.settings, fieldTimeEqualizerEnabled: true, noConsecutivePosition: true }, inningLogs, game.date, game.tournamentId, team.styleOfPlay);
            break;
          case 'sub_bench':
            finalLineup = applySubBenchMode(lineupWithPitcher, inningLogs);
            break;
          default:
            finalLineup = generateAutoLineup(lineupWithPitcher, team.settings, inningLogs, game.date, game.tournamentId, team.styleOfPlay);
        }

        setLineup(finalLineup);
        // Not awaited: the write settles only on server ack, which would hold the
        // Auto buttons disabled at a field with no signal.
        setDoc(doc(db, `teams/${teamId}/games`, game.id), { lineup: deepClean(finalLineup) }, { merge: true })
          .catch(err => console.error("Failed to save auto lineup", err));

        // Warn if the auto-generated pitcher is on mandatory rest
        const autoPitcher = finalLineup.find((p: any) => p.position === 'P');
        if (autoPitcher) {
          const pitcherFull = players.find((p: any) => p.id === autoPitcher.id);
          if (pitcherFull) {
            const log: any[] = pitcherFull.pitchingLog || [];
            if (log.length > 0) {
              const sorted = [...log].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
              const last = sorted[0];
              if (last?.eligibleDate) {
                const gDate = new Date(game.date); gDate.setHours(0, 0, 0, 0);
                const eligible = new Date(last.eligibleDate); eligible.setHours(0, 0, 0, 0);
                if (eligible > gDate) {
                  const dateStr = eligible.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  setAlertModal({
                    title: 'Pitcher on Mandatory Rest',
                    message: `${pitcherFull.name} is not available to pitch until ${dateStr}. Please verify pitch count rules before game time.`,
                    buttonLabel: 'Close',
                  });
                }
              }
            }
          }
        }

      } catch (err) {
        console.error("Failed to auto-generate lineup", err);
      } finally {
        setIsGeneratingLineup(false);
      }
    }, 100);
  };

  const handlePlayerClick = (playerId: string) => {
    if (selectedPlayerId === playerId) {
      setSelectedPlayerId(null);
    } else {
      tapLight(); // selecting a player on the bench / roster list
      setSelectedPlayerId(playerId);
    }
  };

  const isPlayersPitch = team.styleOfPlay === 'playerspitch';

  const handlePositionClick = async (pos: string) => {
    // Only restrict 'P' and prevent moving the pitcher in Players Pitch style
    if (isPlayersPitch) {
      // Only send the coach to Change Pitcher when there is someone to change.
      // With an empty mound (e.g. auto-generate left it open) that redirect was a
      // dead end: Change Pitcher's first step logs the CURRENT pitcher's count and
      // won't advance without one. Assigning straight to an empty mound is safe -
      // there is no incumbent whose pitches could go unlogged.
      if (pos === 'P' && currentPitcher) {
        setAlertModal({ title: 'Pitcher Position', message: 'Use the Change Pitcher button to swap pitchers.' });
        return;
      }
      const selectedPlayer = lineup.find((p: any) => p.id === selectedPlayerId);
      if (selectedPlayer?.position === 'P') {
        setAlertModal({ title: 'Active Pitcher', message: `${currentPitcher?.name ?? 'The pitcher'} is on the mound. Use Change Pitcher to move them.` });
        setSelectedPlayerId(null);
        return;
      }
    }

    if (selectedPlayerId) {
      const selectedPlayer = lineup.find((p: any) => p.id === selectedPlayerId);

      // Pitch count blocks catching
      if (pos === 'C' && team.settings?.pitchCountBlocksCatchingEnabled) {
        const pitchesThrown = pitchCounts[selectedPlayerId] ?? 0;
        const blockThreshold = team.settings?.pitchCountBlocksCatching ?? 40;
        if (pitchesThrown >= blockThreshold) {
          setAlertModal({ title: 'Cannot Play Catcher', message: `${selectedPlayer?.name} has thrown ${pitchesThrown} pitches and is not eligible to catch per league rules.` });
          setSelectedPlayerId(null);
          return;
        }
      }

      // Pitcher → Catcher restriction
      if (pos === 'C' && team.settings?.pitcherToCatcherRestriction && pitchedThisGame.includes(selectedPlayerId)) {
        setAlertModal({ title: 'Cannot Play Catcher', message: `${selectedPlayer?.name} has pitched in this game and cannot play catcher.` });
        setSelectedPlayerId(null);
        return;
      }

      // Catcher → Pitcher restriction
      if (pos === 'P' && team.settings?.catcherToPitcherRestriction) {
        const caughtThisGame =
          selectedPlayer?.position === 'C' ||
          Object.values(inningLogs).some((log: any) =>
            log.find((e: any) => e.id === selectedPlayerId && e.position === 'C')
          );
        if (caughtThisGame) {
          setAlertModal({ title: 'Cannot Pitch', message: `${selectedPlayer?.name} has caught in this game and is not eligible to pitch per league rules.` });
          setSelectedPlayerId(null);
          return;
        }
      }

      const playerAtPos = getPlayerAt(pos);
      // ... rest of movement logic ...
      if (playerAtPos?.id === selectedPlayerId) {
        setSelectedPlayerId(null);
        return;
      }

      const newLineup = lineup.map(p => {
        if (p.id === selectedPlayerId) {
          return { ...p, position: pos };
        }
        if (p.id === playerAtPos?.id) {
          return { ...p, position: 'BENCH' };
        }
        return p;
      });

      tapLight(); // a player placed onto a field position
      setLineup(newLineup);
      setSelectedPlayerId(null);

      // Warn if a player on mandatory rest is assigned to P
      if (pos === 'P') {
        const pitcherFull = players.find((p: any) => p.id === selectedPlayerId);
        if (pitcherFull) {
          const log: any[] = pitcherFull.pitchingLog || [];
          if (log.length > 0) {
            const sorted = [...log].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
            const last = sorted[0];
            if (last?.eligibleDate) {
              const gDate = new Date(game.date); gDate.setHours(0, 0, 0, 0);
              const eligible = new Date(last.eligibleDate); eligible.setHours(0, 0, 0, 0);
              if (eligible > gDate) {
                const dateStr = eligible.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                setAlertModal({
                  title: 'Pitcher on Mandatory Rest',
                  message: `${pitcherFull.name} is not available to pitch until ${dateStr}. Please verify pitch count rules before game time.`,
                  buttonLabel: 'Close',
                });
              }
            }
          }
        }
      }

      try {
        await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
          lineup: deepClean(newLineup)
        }, { merge: true });
      } catch (error) {
        console.error("Failed to update lineup", error);
      }
    } else {
      const playerAtPos = getPlayerAt(pos);
      if (playerAtPos) {
        tapLight(); // selecting the player standing at a field position
        setSelectedPlayerId(playerAtPos.id);
      } else {
        setEmptyPositionToFill(pos);
      }
    }
  };

  const handleBenchClick = async () => {
    if (selectedPlayerId) {
      const selectedPlayer = lineup.find((p: any) => p.id === selectedPlayerId);
      if (selectedPlayer?.position === 'P') {
        setAlertModal({ title: 'Active Pitcher', message: `${currentPitcher?.name ?? 'The pitcher'} is on the mound. Use Change Pitcher to move them.` });
        setSelectedPlayerId(null);
        return;
      }
      // Tapping the bench with a player selected sends them to the bench, the
      // one thing that gesture ever meant. Roster changes (a kid who left or
      // showed up late) now live in the game menu's Add / Remove Player panel.
      const newLineup = lineup.map(p =>
        p.id === selectedPlayerId ? { ...p, position: 'BENCH' } : p
      );
      setLineup(newLineup);
      setSelectedPlayerId(null);
      try {
        await setDoc(doc(db, `teams/${teamId}/games`, game.id), { lineup: deepClean(newLineup) }, { merge: true });
      } catch (error) {
        console.error('Failed to update lineup', error);
      }
    }
  };

  // Remove a player from today's game. They keep their innings already logged;
  // fairness simply stops expecting more from them (see removedPlayerIds).
  const commitRemovePlayer = async (playerId: string) => {
    const newLineup = lineup.filter(p => p.id !== playerId);
    const newRemoved = [...new Set([...removedPlayerIds, playerId])];
    setLineup(newLineup);
    setRemovedPlayerIds(newRemoved);
    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id),
        { lineup: deepClean(newLineup), removedPlayerIds: newRemoved }, { merge: true });
    } catch (error) {
      console.error('Failed to remove player', error);
    }
  };

  // Removing is instant too. A vacated fielding position is simply left open on
  // the diamond, where the coach can tap it to fill it (or run Auto Lineup). We
  // deliberately do NOT interrogate the coach for a replacement here: a mistap
  // must cost one tap to undo, not a forced substitution followed by a re-add.
  const handleRemovePlayer = (player: any) => {
    const entry = lineup.find((p: any) => p.id === player.id);
    if (entry && entry.position === 'P' && isPlayersPitch) {
      setAlertModal({
        title: 'Active Pitcher',
        message: `${entry.name || 'This player'} is on the mound. Use Change Pitcher first, then remove them.`,
      });
      return;
    }
    commitRemovePlayer(player.id);
  };

  const handleAddLatePlayer = (player: any) => {
    setIsLatePlayerModalOpen(false);
    confirmAddLatePlayer(false, player);
  };

  // Toggling a player back on is instant: they slot into their original spot in
  // the batting order and land on the bench. No question, because the toggle is
  // free to flip back if it was a mistap. `atEnd` is kept for the one caller that
  // deliberately tacks a player onto the end of the order.
  const confirmAddLatePlayer = async (atEnd: boolean, playerArg?: any) => {
    const player = playerArg;
    if (!player) return;

    // Carry the whole roster doc across. Hand-picking fields used to drop
    // pitchingLog and stats, and turned a missing `positions` into `{}` - which
    // reads as "rated 0 everywhere" rather than "unknown", so the player became
    // ineligible for every position in the lineup engine.
    const newPlayer = { ...player, position: 'BENCH' };

    let newLineup: any[];
    if (atEnd) {
      newLineup = [...lineup, newPlayer];
    } else {
      // Find the player's original index in game.plan.playerOrder or fall back to roster order
      const originalOrder: string[] = game.plan?.playerOrder || players.map((p: any) => p.id);
      const insertIndex = originalOrder.indexOf(player.id);
      if (insertIndex === -1) {
        newLineup = [...lineup, newPlayer];
      } else {
        // Insert before the first lineup player whose original index is greater
        const spliceAt = lineup.findIndex((lp: any) => {
          const idx = originalOrder.indexOf(lp.id);
          return idx !== -1 && idx > insertIndex;
        });
        if (spliceAt === -1) {
          newLineup = [...lineup, newPlayer];
        } else {
          newLineup = [...lineup.slice(0, spliceAt), newPlayer, ...lineup.slice(spliceAt)];
        }
      }
    }

    const newLateArrivals = lateArrivals.filter(id => id !== player.id);
    const newRemoved = removedPlayerIds.filter(id => id !== player.id);
    setLineup(newLineup);
    setLateArrivals(newLateArrivals);
    setRemovedPlayerIds(newRemoved);
    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id),
        { lineup: deepClean(newLineup), lateArrivals: newLateArrivals, removedPlayerIds: newRemoved }, { merge: true });
    } catch (error) {
      console.error('Failed to add late player', error);
    }
  };

  // ── Live Fairness Check (Checkpoint A) ──────────────────────────────────────
  // Computes observation-only findings against the inning that just ended, using
  // the same inningLogs the lineup engine reads. Gated entirely on whether the
  // coach has any Field Time rule enabled. State holds the findings to show in the
  // 'fairness_review' step of the inning-flow modal.
  const [fairnessFindings, setFairnessFindings] = useState<FairnessFinding[]>([]);

  const rosterNames: Record<string, string> = {};
  players.forEach((p: any) => { rosterNames[p.id] = p.name; });

  // Total innings planned for this game — used so the min-innings rule can warn
  // "behind pace" before it's too late. Fall back to 6 (standard youth game).
  const plannedFromPlan = game.plan?.innings ? Object.keys(game.plan.innings).length : 0;
  const totalInningsPlanned: number = game.innings || plannedFromPlan || 6;

  // Route to the fairness review step when any rule is on; otherwise straight to
  // defense (byte-for-byte the old behavior). `logs` is the freshly-saved logs so
  // the analysis includes the inning that just ended.
  const goToDefenseOrFairness = (logs: Record<number, any[]>) => {
    if (anyFairnessRuleOn(team.settings)) {
      const lastKey = Math.max(...Object.keys(logs).map(Number));
      const lastLineup = (logs[lastKey] || []).map((e: any) => ({ id: e.id, position: e.position }));
      setFairnessFindings(
        analyzeFairness(lastLineup, logs, team.settings, rosterNames, totalInningsPlanned, removedPlayerIds)
      );
      setInningFlowStep('fairness_review');
    } else {
      setInningFlowStep('set_defense');
    }
  };

  // Auto-Balance from the Fairness Check box: run the SAME OVR-aware, fairness-
  // respecting engine the "Auto" lineup modes use (generateAutoLineup with the
  // team's real settings), apply it to the next inning, then drop the coach onto
  // the diamond ('set_defense') with it pre-filled — still fully editable.
  const handleAutoBalanceFromFairness = async () => {
    setIsGeneratingLineup(true);
    try {
      const lineupWithPitcher = hydrateLineupForEngine();
      const balanced = generateAutoLineup(
        lineupWithPitcher,
        team.settings,
        inningLogs,
        game.date,
        game.tournamentId,
        team.styleOfPlay
      );
      setLineup(balanced);
      // Not awaited, so the inning flow moves on with no signal.
      setDoc(doc(db, `teams/${teamId}/games`, game.id), { lineup: deepClean(balanced) }, { merge: true })
        .catch(err => console.error('Failed to save auto-balanced lineup', err));
    } catch (err) {
      console.error('Auto-balance failed', err);
    } finally {
      setIsGeneratingLineup(false);
      setInningFlowStep('set_defense');
    }
  };

  const handleEndInningClick = async () => {
    // Save the current lineup as the log for this inning
    const currentLog = lineup.map(p => ({ id: p.id, position: p.position }));
    const newLogs = { ...inningLogs, [currentInning]: currentLog };
    setInningLogs(newLogs);

    // Not awaited: End Inning used to wait for the server before opening the
    // pitch count step, which froze the inning at a field with no signal.
    setDoc(doc(db, `teams/${teamId}/games`, game.id), {
      inningLogs: newLogs
    }, { merge: true }).catch(error => console.error("Failed to save inning log", error));

    // Bypass pitch logging for non-Players Pitch styles
    if (currentPitcher && isPlayersPitch) {
      setCurrentLoggingPitcherId(currentPitcher.id);
      setInningFlowStep('log_pitches');
    } else {
      // Direct transition to defense/next inning
      const nextInning = currentInning + 1;
      // ...
      if (game.plan && game.plan.innings && game.plan.innings[(nextInning - 1).toString()]) {
        const plannedInning = game.plan.innings[(nextInning - 1).toString()];
        // Keep whoever is on the mound there across the inning change. The other
        // two rollover paths preserve their pitcher; this one used to bench every
        // 'P' unconditionally, so a planned pitcher was wiped each inning.
        const livePitcherId = lineup.find((p: any) => p.position === 'P')?.id;
        // Whoever the plan had at P takes the live pitcher's planned spot, so
        // blocking a second pitcher never leaves a fielding position empty.
        const livePlannedPos = plannedInning.find((pp: any) => pp.id === livePitcherId)?.position;
        const handOff = livePlannedPos && livePlannedPos !== 'P' ? livePlannedPos : 'BENCH';
        const nextLineup = lineup.map(p => {
          if (livePitcherId && p.id === livePitcherId) return p;
          const plannedPos = plannedInning.find((pp: any) => pp.id === p.id);
          let targetPos = plannedPos ? plannedPos.position : 'BENCH';
          // Only one pitcher at a time: block anyone else the plan puts at P.
          if (targetPos === 'P' && livePitcherId) targetPos = handOff;
          return { ...p, position: targetPos };
        });
        setLineup(nextLineup);
        setDoc(doc(db, `teams/${teamId}/games`, game.id), { lineup: deepClean(nextLineup) }, { merge: true }).catch(console.error);
      }
      goToDefenseOrFairness(newLogs);
    }
  };

  const handleLastBatterChange = async (playerId: string) => {
    setLastBatterId(playerId);
    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        lastBatterId: playerId
      }, { merge: true });
    } catch (error) {
      console.error("Failed to update last batter", error);
    }
  };

  const handleSavePitches = () => {
    const addedPitches = parseInt(inningPitches, 10);
    if (!isNaN(addedPitches) && currentLoggingPitcherId) {
      setInningPitchLogs(prev => [...prev, { pitcherId: currentLoggingPitcherId, pitches: addedPitches }]);
    }
    setInningPitches('');
    setInningFlowStep('next_pitcher');
  };

  const handleAddAnotherPitcher = () => {
    const addedPitches = parseInt(inningPitches, 10);
    if (!isNaN(addedPitches) && currentLoggingPitcherId) {
      setInningPitchLogs(prev => [...prev, { pitcherId: currentLoggingPitcherId, pitches: addedPitches }]);
    }
    setInningPitches('');
    setInningFlowStep('select_pitcher');
  };

  const handlePitcherContinue = async (willContinue: boolean) => {
    setWillPitcherContinue(willContinue);
    
    if (willContinue) {
      const nextInning = currentInning + 1;
      let nextLineup = [...lineup];
      
      // Apply plan for next inning
      if (game.plan && game.plan.innings && game.plan.innings[(nextInning - 1).toString()]) {
        const plannedInning = game.plan.innings[(nextInning - 1).toString()];
        const livePitcherId = lineup.find((p: any) => p.position === 'P')?.id;

        // The coach kept his pitcher, so whoever the plan had on the mound takes
        // the pitcher's planned spot instead. Benching them left that spot empty.
        const livePlannedPos = plannedInning.find((pp: any) => pp.id === livePitcherId)?.position;
        const handOff = livePlannedPos && livePlannedPos !== 'P' ? livePlannedPos : 'BENCH';

        nextLineup = nextLineup.map(p => {
          if (p.id === livePitcherId) return p;
          const plannedPos = plannedInning.find((pp: any) => pp.id === p.id);
          let targetPos = plannedPos ? plannedPos.position : 'BENCH';
          if (targetPos === 'P') targetPos = handOff;
          return { ...p, position: targetPos };
        });
        setLineup(nextLineup);
      }

      goToDefenseOrFairness(inningLogs);

      // Save to DB
      try {
        await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
          lineup: deepClean(nextLineup)
        }, { merge: true });
      } catch (error) {
        console.error("Failed to update lineup for next inning", error);
      }
    } else {
      // Pitcher is leaving. Mark as pitched and open selection.
      let newPitchedThisGame = [...pitchedThisGame];
      if (currentPitcher && !newPitchedThisGame.includes(currentPitcher.id)) {
        newPitchedThisGame.push(currentPitcher.id);
        setPitchedThisGame(newPitchedThisGame);
      }
      
      setChangePitcherStep('select_pitcher');
      setInningFlowStep(null); // Close the inning flow modal
    }
  };

  // The actual advance-to-next-inning work, split out so the Checkpoint B
  // fairness re-check can gate it without duplicating the logic.
  const proceedToNextInning = async () => {
    const newCounts = { ...pitchCounts };
    inningPitchLogs.forEach(log => {
      newCounts[log.pitcherId] = (newCounts[log.pitcherId] || 0) + log.pitches;
    });
    setPitchCounts(newCounts);

    const nextInning = currentInning + 1;

    setCurrentInning(nextInning);
    setInningFlowStep(null);
    setInningPitchLogs([]);
    setWillPitcherContinue(null);
    setCurrentLoggingPitcherId(null);

    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        pitchCounts: newCounts,
        currentInning: nextInning,
        lineup: deepClean(lineup)
      }, { merge: true });
    } catch (error) {
      console.error("Failed to update game state", error);
    }
  };

  const handleReadyForNextInning = async () => {
    // Ensure a pitcher is assigned before moving to next inning
    const pitcher = lineup.find(p => p.position === 'P');
    if (!pitcher && !team.settings?.noPitcher) {
      setAlertModal({ title: 'No Pitcher Assigned', message: `Assign a pitcher before starting Inning ${currentInning + 1}.` });
      return;
    }

    // ── Checkpoint B: Fairness re-check (non-blocking) ──────────────────────────
    // Re-run the fairness analysis on the lineup the coach just arranged (his
    // manual edits, or an Auto Change he tweaked). If a gap remains — including
    // any engine no-repeat-bench leak — alert him before the inning starts, but
    // NEVER block: "Start anyway" proceeds, "Adjust" returns to the diamond.
    if (anyFairnessRuleOn(team.settings)) {
      const findings = analyzeFairness(
        lineup.map((p: any) => ({ id: p.id, position: p.position })),
        inningLogs,
        team.settings,
        rosterNames,
        totalInningsPlanned,
        removedPlayerIds,
        true, // this lineup is the UPCOMING inning, not one already played
      );
      if (findings.length > 0) {
        setConfirmModal({
          title: `Fairness: Inning ${currentInning + 1}`,
          message: findings.slice(0, 3).map(f => `• ${f.message}`).join('\n'),
          confirmLabel: 'Start anyway',
          cancelLabel: 'Adjust',
          onConfirm: () => { setConfirmModal(null); proceedToNextInning(); },
        });
        return;
      }
    }

    proceedToNextInning();
  };

  const handleFinishGame = async (csvData?: any[]) => {
    setIsFinishingGame(true);
    setFinishGameError(null);
    try {
      const awayScoreNum = parseInt(awayScore) || 0;
      const homeScoreNum = parseInt(homeScore) || 0;

      const myScoreNum = game.isHome ? homeScoreNum : awayScoreNum;
      const oppScoreNum = game.isHome ? awayScoreNum : homeScoreNum;

      const outcome = myScoreNum > oppScoreNum ? 'W' : myScoreNum < oppScoreNum ? 'L' : 'T';
      const result = {
        myScore: myScoreNum,
        opponentScore: oppScoreNum,
        outcome
      };
      setGameResult(result);

      // Accumulate all updates keyed by document path so each doc is written
      // exactly once — Firestore rejects batches with duplicate document refs.
      // Strip empty-string keys from pitchCounts — they sneak in if the "Add
      // Pitcher" select fires with its placeholder value and Firestore rejects them.
      const sanitizedPitchCounts = Object.fromEntries(
        Object.entries(finalPitchCounts).filter(([k]) => k !== '')
      );
      const gameUpdates: Record<string, any> = {
        status: 'completed',
        result,
        pitchCounts: sanitizedPitchCounts,
        clockEndsAt: null,
        updatedAt: new Date().toISOString(),
        gameBallRecipients: selectedGameBallIds,
      };
      // The inning in progress, when End Inning wasn't tapped first (G10).
      const finalLogs = logsWithInningInProgress(inningLogs, currentInning, lineup);
      if (finalLogs !== inningLogs) gameUpdates.inningLogs = finalLogs;
      // playerUpdates[playerId] accumulates all field patches for that player
      const playerUpdates: Record<string, Record<string, any>> = {};
      const mergePlayer = (id: string, patch: Record<string, any>) => {
        playerUpdates[id] = { ...playerUpdates[id], ...patch };
      };

      // 1. Process Pitching Logs
      // Use the TOURNAMENT-aware ruleset + custom rules so the rest days we freeze
      // onto each log entry reflect the rules actually in force for this game. Using
      // the season ruleset here was a bug: an outing in a Custom (e.g. no-rest)
      // tournament got stamped with season rest, leaving the pitcher wrongly blocked.
      const ruleset = PITCH_COUNT_RULESETS[tournamentRulesetOverride || team.settings?.pitchCountRuleset || 'little_league'] || PITCH_COUNT_RULESETS['little_league'];
      const ageGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
      const gameDate = new Date(game.date);

      for (const [playerId, count] of Object.entries(sanitizedPitchCounts)) {
        if (count > 0) {
          const player = players.find(p => p.id === playerId);
          if (player) {
            const ageRules = (ruleset.id === 'custom' && tournamentCustomRules)
              ? tournamentCustomRules
              : (ruleset.ageGroups[ageGroup] || ruleset.ageGroups['12U'] || { dailyMax: 85, restThresholds: [] });

            const restThresholds = ageRules.restThresholds || [];
            const restDays = calculateRestDays(count, restThresholds);
            const eligibleDate = new Date(gameDate);
            eligibleDate.setDate(eligibleDate.getDate() + restDays + 1);
            eligibleDate.setHours(0, 0, 0, 0);

            const logEntry = {
              date: gameDate.toISOString(),
              pitchCount: count,
              requiredRestDays: restDays,
              eligibleDate: eligibleDate.toISOString(),
              gameId: game.id,
              tournamentId: game.tournamentId || null,
              opponent: game.opponent || 'Unknown',
              inningsPitched: 0,
              outsPitched: 0,
            };

            const currentLog = player.pitchingLog || [];
            const filteredLog = currentLog.filter((entry: any) => entry.gameId !== game.id);
            mergePlayer(playerId, { pitchingLog: [...filteredLog, logEntry] });
          }
        }
      }

      // 2. Process CSV data if provided
      if (csvData && csvData.length > 0) {
        for (const row of csvData) {
          if (!row.Number && !row.Last) continue;

          const matchedPlayer = players.find((p: any) =>
            p.number === row.Number ||
            (p.name && p.name.toLowerCase().includes(row.Last?.toLowerCase()))
          );

          if (matchedPlayer) {
            mergePlayer(matchedPlayer.id, {
              stats: {
                gp: parseInt(row.GP) || 0,
                pa: parseInt(row.PA) || 0,
                ab: parseInt(row.AB) || 0,
                avg: parseFloat(row.AVG) || 0,
                obp: parseFloat(row.OBP) || 0,
                ops: parseFloat(row.OPS) || 0,
                h: parseInt(row.H) || 0,
                bb: parseInt(row.BB) || 0,
                so: parseInt(row.SO) || 0,
                rbi: parseInt(row.RBI) || 0,
                r: parseInt(row.R) || 0,
              }
            });
          }
        }
      }

      // 3. Aggregate season bench innings for Field Time Equalizer
      if (finalLogs && Object.keys(finalLogs).length > 0) {
        const sortedKeys = Object.keys(finalLogs).map(Number).sort((a, b) => a - b);

        for (const player of players) {
          let benchThisGame = 0;
          sortedKeys.forEach(k => {
            const entry = finalLogs[k]?.find((l: any) => l.id === player.id);
            if (entry && (entry.position === 'BENCH' || entry.position === 'EH')) {
              benchThisGame++;
            }
          });

          const priorBenchLog: Record<string, number> =
            player.benchLog && typeof player.benchLog === 'object'
              ? player.benchLog
              : {};
          const newBenchLog = { ...priorBenchLog, [game.id]: benchThisGame };
          const seasonBenchInnings = Object.values(newBenchLog).reduce(
            (sum: number, n) => sum + (n as number), 0
          );
          mergePlayer(player.id, { benchLog: newBenchLog, seasonBenchInnings });
        }
      }

      // 3b. Catcher development — roll this game's passed balls / blocks into
      // per-player season logs, mirroring the gameBallLog pattern. Each log is a
      // { [gameId]: count } map so re-finishing a game overwrites cleanly.
      if (catcherEvents.length > 0) {
        const pbByCatcher: Record<string, number> = {};
        const blockByCatcher: Record<string, number> = {};
        for (const e of catcherEvents) {
          if (!e.catcherId) continue;
          if (e.type === 'PB') pbByCatcher[e.catcherId] = (pbByCatcher[e.catcherId] || 0) + 1;
          else if (e.type === 'BLOCK') blockByCatcher[e.catcherId] = (blockByCatcher[e.catcherId] || 0) + 1;
        }
        const catcherIds = new Set([...Object.keys(pbByCatcher), ...Object.keys(blockByCatcher)]);
        for (const pid of catcherIds) {
          const player = players.find((p: any) => p.id === pid);
          if (!player) continue;
          const priorPBLog: Record<string, number> = player.passedBallLog ?? {};
          const priorBlockLog: Record<string, number> = player.blockLog ?? {};
          const newPBLog = { ...priorPBLog, [game.id]: pbByCatcher[pid] || 0 };
          const newBlockLog = { ...priorBlockLog, [game.id]: blockByCatcher[pid] || 0 };
          const seasonPassedBalls = Object.values(newPBLog).reduce((s: number, n) => s + (n as number), 0);
          const seasonBlocks = Object.values(newBlockLog).reduce((s: number, n) => s + (n as number), 0);
          mergePlayer(pid, { passedBallLog: newPBLog, blockLog: newBlockLog, seasonPassedBalls, seasonBlocks });
        }
      }

      // 4. Game ball recipients
      for (const pid of selectedGameBallIds) {
        const player = players.find((p: any) => p.id === pid);
        if (!player) continue;
        const priorLog: Record<string, number> = player.gameBallLog ?? {};
        const newLog = { ...priorLog, [game.id]: 1 };
        const seasonGameBalls = Object.values(newLog).reduce((sum: number, n) => sum + (n as number), 0);
        mergePlayer(pid, { gameBallLog: newLog, seasonGameBalls });
      }

      // Commit — one write per document
      const batch = writeBatch(db);
      const gameRef = doc(db, `teams/${teamId}/games`, game.id);
      batch.set(gameRef, gameUpdates, { merge: true });
      for (const [playerId, patch] of Object.entries(playerUpdates)) {
        const playerRef = doc(db, `teams/${teamId}/players`, playerId);
        batch.set(playerRef, { ...patch, updatedAt: new Date().toISOString() }, { merge: true });
      }
      // Save and exit even with no signal: give the server a moment to confirm
      // (a real error on a good connection still lands in the catch and shows
      // Retry), then move on with the batch queued on this phone. It uploads,
      // game and player docs together, when the connection returns.
      await saveLocalFirst(batch.commit(), 'End Game');
      // Activation funnel. Logged once the game is saved (confirmed, or queued
      // offline), never for a commit that errored. `source` separates a game scored live in Dugout
      // from one that arrives via import: without it the funnel cannot tell a
      // coach who plays but never imports from one who does neither, which is
      // exactly the distinction A3 turns on.
      track('game_logged', {
        source: 'live',
        innings: Object.keys(finalLogs ?? {}).length,
        hasPitchCounts: Object.keys(sanitizedPitchCounts).length > 0,
      });
      pagerEnded.current = true;
      endGameActivity(); // remove the Live Activity when the game ends

      if (game.tournamentId) {
        if (game.tournamentPhase === 'bracket') {
          setShowCelebration(true);
        } else {
          setShowAdvisor(true);
        }
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error in handleFinishGame:", error);
      setFinishGameError('Failed to save game. Check your connection and tap Retry.');
    } finally {
      setIsFinishingGame(false);
    }
  };

  const handleFillEmptyPosition = async (playerId: string) => {
    if (!emptyPositionToFill) return;
    const newLineup = lineup.map(p => {
      if (p.id === playerId) {
        return { ...p, position: emptyPositionToFill };
      }
      return p;
    });
    setLineup(newLineup);
    setEmptyPositionToFill(null);
    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        lineup: deepClean(newLineup)
      }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `teams/${teamId}/games`);
    }
  };

  // Guard the swap: if the incoming pitcher is on mandatory rest for today's
  // game, ask the coach to confirm BEFORE anything commits. They can still bring
  // the arm in (the coach has final say — bench-coach voice), but never by
  // accident. Eligible arms go straight through.
  const handleSelectNewPitcher = (newPitcherId: string, inningTransition?: boolean) => {
    if (!currentPitcher) return;
    const rest = checkPitcherRest(newPitcherId);
    if (rest.onRest) {
      const newPitcherFull = players.find((p: any) => p.id === newPitcherId);
      const name = newPitcherFull?.name || 'This pitcher';
      const untilStr = rest.until ? ` until ${rest.until}` : '';
      setConfirmModal({
        title: 'On Mandatory Rest',
        message: `${name} is on mandatory rest${untilStr} under your pitch count rules. Bringing them in now goes against the rest requirement. Bring them in anyway?`,
        confirmLabel: 'Bring In Anyway',
        onConfirm: () => { setConfirmModal(null); commitNewPitcher(newPitcherId, inningTransition); },
      });
      return;
    }
    commitNewPitcher(newPitcherId, inningTransition);
  };

  /**
   * "Bring in Jake" from the between-innings prompt: the same pitching change
   * as answering No and picking him, in one tap. `inningTransition` is passed
   * explicitly because setWillPitcherContinue has not landed in this tick, and
   * commitNewPitcher needs to know to apply the next inning's planned defense.
   */
  const handleBringInPlannedPitcher = (newPitcherId: string) => {
    setWillPitcherContinue(false);
    setInningFlowStep(null);
    handleSelectNewPitcher(newPitcherId, true);
  };

  // Coach is sending an un-scouted player to the mound via "See More Players".
  // Tag them as a pitcher on the roster (P rating 1, only if currently 0) so they
  // appear in the bullpen automatically next game. We deliberately do NOT touch
  // pitching attributes (velocity/control) — those are scouting measurements and
  // faking them would skew OVR/xOVR. Then run the normal swap (rest guard included).
  const handleSelectFromMore = async (newPitcherId: string) => {
    const current = players.find((p: any) => p.id === newPitcherId);
    const currentP = current?.positions?.P ?? current?.ratings?.P ?? 0;
    if (currentP < 1) {
      // Reflect the tag in the live lineup immediately so the engine sees them as a pitcher.
      setLineup(prev => prev.map((p: any) =>
        p.id === newPitcherId
          ? { ...p, positions: { ...(p.positions ?? {}), P: 1 } }
          : p
      ));
      // Not awaited: waiting on the server held up the pitching change itself
      // at a field with no signal.
      setDoc(
        doc(db, `teams/${teamId}/players`, newPitcherId),
        { positions: { ...(current?.positions ?? {}), P: 1 }, updatedAt: new Date().toISOString() },
        { merge: true }
      ).catch(error => console.error("Failed to tag new pitcher", error));
    }
    handleSelectNewPitcher(newPitcherId);
  };

  const commitNewPitcher = async (newPitcherId: string, inningTransition = willPitcherContinue === false) => {
    if (!currentPitcher) return;

    let newLineup = lineup.map(p => {
      if (p.id === newPitcherId) return { ...p, position: 'P' };
      if (p.id === currentPitcher.id) return { ...p, position: 'BENCH' };
      return p;
    });

    // The outgoing pitcher is included for the one-tap "Bring in" path, which
    // skips handlePitcherContinue(false), where he is normally marked as pitched.
    const newPitchedThisGame = [...new Set([
      ...pitchedThisGame,
      ...(inningTransition ? [currentPitcher.id] : []),
      newPitcherId,
    ])];
    
    // Handle Inning Transition Plan Application
    if (inningTransition) {
      const nextInning = currentInning + 1;
      if (game.plan && game.plan.innings && game.plan.innings[(nextInning - 1).toString()]) {
        const plannedInning = game.plan.innings[(nextInning - 1).toString()];
        newLineup = newLineup.map(p => {
          // Keep the new pitcher at P
          if (p.id === newPitcherId) return { ...p, position: 'P' };
          
          const plannedPos = plannedInning.find((pp: any) => pp.id === p.id);
          let targetPos = plannedPos ? plannedPos.position : 'BENCH';
          
          // Block anyone else from being P
          if (targetPos === 'P') targetPos = 'BENCH';
          
          return { ...p, position: targetPos };
        });
      }
      goToDefenseOrFairness(inningLogs);
      setWillPitcherContinue(null);
    }

    setLineup(newLineup);
    setPitchedThisGame(newPitchedThisGame);
    setChangePitcherStep(null);
    setShowMorePitchers(false);

    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        lineup: deepClean(newLineup),
        pitchedThisGame: newPitchedThisGame
      }, { merge: true });
    } catch (error) {
      console.error("Failed to change pitcher", error);
    }
  };

  const handleLogChangePitcher = async () => {
    const addedPitches = parseInt(changePitcherInningPitches, 10);
    if (isNaN(addedPitches) || !currentPitcher) return;

    snapshotPitcherState();

    const newCounts = {
      ...pitchCounts,
      [currentPitcher.id]: (pitchCounts[currentPitcher.id] || 0) + addedPitches
    };

    setPitchCounts(newCounts);
    setChangePitcherInningPitches('');
    setChangePitcherStep('select_pitcher');

    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        pitchCounts: newCounts
      }, { merge: true });
    } catch (error) {
      console.error("Failed to log mid-inning pitches", error);
    }
  };

  const snapshotPitcherState = () => {
    setPrevPitcherState({ lineup: [...lineup], pitchCounts: { ...pitchCounts }, pitchedThisGame: [...pitchedThisGame] });
  };

  const handleUndoPitcherChange = async () => {
    if (!prevPitcherState) return;
    setLineup(prevPitcherState.lineup);
    setPitchCounts(prevPitcherState.pitchCounts);
    setPitchedThisGame(prevPitcherState.pitchedThisGame);
    setPrevPitcherState(null);
    try {
      await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        lineup: deepClean(prevPitcherState.lineup),
        pitchCounts: prevPitcherState.pitchCounts,
        pitchedThisGame: prevPitcherState.pitchedThisGame
      }, { merge: true });
    } catch (error) {
      console.error("Failed to undo pitcher change", error);
    }
  };

  // The pitch tracker handlers below save in the background (no await). The
  // tracker locks Ball/Strike until these resolve, and a Firestore write doesn't
  // resolve until the SERVER acknowledges it — at a field with weak signal that
  // can be minutes, which froze pitch logging mid-inning. The write still lands
  // in the local cache immediately and syncs when the connection returns.
  const handleSaveAtBat = async (atBat: any) => {
    const newSequence = [...pitchSequence, atBat];
    setPitchSequence(newSequence);
    setDoc(doc(db, `teams/${teamId}/games`, game.id), {
      pitchSequence: newSequence
    }, { merge: true }).catch(error => console.error("Failed to save at-bat", error));
  };

  const handleDiscardGame = async () => {
    try {
      await saveLocalFirst(setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        status: 'scheduled',
        inningLogs: {},
        pitchCounts: {},
        currentInning: 1,
        pitchedThisGame: [],
        pitchSequence: [],
        catcherEvents: [],
        lastBatterId: null,
      }, { merge: true }), 'Discard Game');
      pagerEnded.current = true;
      endGameActivity();
      onClose();
    } catch (error) {
      console.error('Failed to discard game', error);
    }
  };

  const handleUndoAtBat = async () => {
    if (pitchSequence.length === 0) return;
    const newSequence = pitchSequence.slice(0, -1);
    setPitchSequence(newSequence);
    setDoc(doc(db, `teams/${teamId}/games`, game.id), {
      pitchSequence: newSequence
    }, { merge: true }).catch(error => console.error("Failed to undo at-bat", error));
  };

  // Log a catcher event (passed ball / block) for a specific catcher.
  const handleSaveCatcherEvent = async (type: 'PB' | 'BLOCK', catcherId: string) => {
    const event = {
      id: String(Date.now()),
      catcherId,
      type,
      inning: currentInning,
      timestamp: new Date().toISOString(),
    };
    const newEvents = [...catcherEvents, event];
    setCatcherEvents(newEvents);
    setDoc(doc(db, `teams/${teamId}/games`, game.id), {
      catcherEvents: newEvents
    }, { merge: true }).catch(error => console.error("Failed to save catcher event", error));
  };

  // Undo the most recent catcher event.
  const handleUndoCatcherEvent = async () => {
    if (catcherEvents.length === 0) return;
    const newEvents = catcherEvents.slice(0, -1);
    setCatcherEvents(newEvents);
    setDoc(doc(db, `teams/${teamId}/games`, game.id), {
      catcherEvents: newEvents
    }, { merge: true }).catch(error => console.error("Failed to undo catcher event", error));
  };

  // Assign a player to catcher (position 'C') from the tracker's
  // "who's catching?" prompt, so subsequent events auto-attribute to them.
  const handleAssignCatcher = async (playerId: string) => {
    const target = lineup.find((p: any) => p.id === playerId);
    if (!target) return;
    const newLineup = lineup.map((p: any) => {
      if (p.id === playerId) return { ...p, position: 'C' };
      // Swap: whoever currently holds 'C' takes the incoming player's old spot.
      if (currentCatcher && p.id === currentCatcher.id) return { ...p, position: target.position };
      return p;
    });
    setLineup(newLineup);
    setDoc(doc(db, `teams/${teamId}/games`, game.id), { lineup: deepClean(newLineup) }, { merge: true })
      .catch(error => console.error("Failed to assign catcher", error));
  };

  // Converted off `legacy-invert` (2026-09-10): this screen and everything it
  // renders (DiamondView, the LiveGameEngine/* panels, every modal) are now
  // authored in the semantic tokens from src/index.css, so Tailwind's named
  // colors behave normally here. Do not reintroduce the inversion class.
  /** The pitch tracker, shared by the Pitches tab and the wide screen's right pane. */
  const renderTracker = (onClose: () => void) => (
    <PitchingTrackerPanel
      inline
      pitcher={currentPitcher}
      catcher={currentCatcher}
      currentInning={currentInning}
      pitchSequence={pitchSequence}
      catcherEvents={catcherEvents}
      eligibleCatchers={lineup.filter((p: any) => p.position !== 'BENCH' && p.position !== 'EH')}
      onSaveAtBat={handleSaveAtBat}
      onUndoAtBat={handleUndoAtBat}
      onSaveCatcherEvent={handleSaveCatcherEvent}
      onUndoCatcherEvent={handleUndoCatcherEvent}
      onAssignCatcher={handleAssignCatcher}
      dailyMax={currentPitcherDailyState?.dailyMax}
      priorTodayPitches={currentPitcherDailyState?.priorToday ?? 0}
      pitches={trackerPitches}
      onPitchesChange={setTrackerPitches}
      onClose={onClose}
    />
  );

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 z-50 bg-canvas flex flex-col safe-top safe-x"
    >
      {/* Wide screens (unfolded iPhone Duo, iPad landscape, laptop): the live game
          on the left at phone width, Now | Next on the right, split down the
          middle so nothing sits on the Duo's fold. The left pane's transform
          makes the engine's fixed modals open inside it, not across the fold.
          On phones both wrappers are display: contents, so the layout is exactly
          what it was. */}
      <div className={isWide ? 'flex-1 min-h-0 flex' : 'contents'}>
      <div className={isWide ? 'relative w-1/2 min-w-0 flex flex-col' : 'contents'} style={isWide ? { transform: 'translateZ(0)' } : undefined}>
      {/* Practice Mode banner — rendered as a flex item (not an overlay) so
          it occupies its own row at the top of the engine. The scrollable
          content below respects the banner's height naturally. */}
      {isPracticeMode && (
        <div className="shrink-0 bg-amber-400 text-ink text-center py-1.5 print:hidden">
          <span className="text-[10px] font-black uppercase tracking-widest">
            DEMO MODE: Nothing is being saved
          </span>
        </div>
      )}

      {/* Game clock bar — pinned, glanceable. Only when the feature is enabled. */}
      {team.settings?.gameClockEnabled && (
        <div className="shrink-0 px-4 py-2 bg-raised border-b border-hairline flex items-center justify-center print:hidden">
          {clock.isRunning ? (
            <button
              onClick={() => { setClockMin(String(game.clockDurationMin ?? team.settings?.defaultGameClockMinutes ?? 75)); setClockModal(true); }}
              className="flex items-center gap-2"
            >
              <Clock className={cn("w-4 h-4", clockUrgent ? "text-red-500" : "text-emerald-400")} />
              <span className={cn("text-lg font-black tabular-nums", clockUrgent ? "text-red-500" : "text-ink")}>
                {clock.isExpired ? "TIME" : clock.display}
              </span>
            </button>
          ) : (
            <button
              onClick={() => { setClockMin(String(team.settings?.defaultGameClockMinutes ?? 75)); setClockModal(true); }}
              className="flex items-center gap-2 text-ink-2 hover:text-ink transition-colors"
            >
              <Clock className="w-4 h-4" />
              <span className="text-[11px] font-black uppercase tracking-widest">Start Clock</span>
            </button>
          )}
        </div>
      )}

      {!isPracticeMode && <SyncNote pending={hasUnsyncedChanges} />}

      {/* Game clock control modal */}
      {clockModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/60 backdrop-blur-sm px-6"
          onClick={() => setClockModal(false)}
        >
          <div
            className="bg-surface border border-emerald-500/40 rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-black uppercase tracking-widest text-accent-ink mb-3">Game Clock</h3>
            <div className="flex items-center gap-3 mb-5">
              <input
                type="number" inputMode="numeric" value={clockMin}
                onChange={e => setClockMin(e.target.value)}
                className="w-24 bg-raised border border-track rounded-xl px-4 py-3 text-ink text-lg font-black text-center focus:outline-none focus:border-emerald-500/50"
              />
              <span className="text-xs font-black uppercase tracking-widest text-ink-2">minutes</span>
            </div>
            <button
              onClick={async () => { setClockModal(false); await startGameClock(parseInt(clockMin) || 0); }}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-ink text-xs font-black uppercase tracking-widest rounded-xl transition-colors mb-2"
            >
              {clock.isRunning ? 'Restart Clock' : 'Start Clock'}
            </button>
            {clock.isRunning && (
              <button
                onClick={async () => { setClockModal(false); await clearGameClock(); }}
                className="w-full py-3 bg-hairline hover:bg-track text-danger text-xs font-black uppercase tracking-widest rounded-xl transition-colors mb-2"
              >
                Clear Clock
              </button>
            )}
            <button
              onClick={() => setClockModal(false)}
              className="w-full py-2.5 text-ink-2 hover:text-ink-2 text-[10px] font-black uppercase tracking-widest transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Scrollable tab content */}
      <div className="flex-1 overflow-y-auto">

        {/* DiamondView always renders — owns the top bar with tabs */}
        <div className="p-4">
          <DiamondView
            team={team}
            currentPitcher={currentPitcher}
            currentPitchCount={currentPitchCount}
            getPlayerAt={getPlayerAt}
            selectedPlayerId={selectedPlayerId}
            handlePositionClick={handlePositionClick}
            inningLabel={inningFlowStep === 'set_defense' ? `Inning ${currentInning + 1}` : `Inning ${currentInning}`}
            benchPlayers={benchPlayers}
            handleBenchClick={handleBenchClick}
            handlePlayerClick={handlePlayerClick}
            onManageRoster={() => setIsRosterManagerOpen(true)}
            lateArrivals={lateArrivals}
            gameInfo={{ opponent: game.opponent, isHome: game.isHome, date: game.date, isTournament: !!game.tournamentId }}
            onPauseExit={onClose}
            onDiscardGame={() => setShowDiscardConfirm(true)}
            onEndGame={() => {
              const currentCounts = { ...pitchCounts };
              inningPitchLogs.forEach(log => {
                currentCounts[log.pitcherId] = (currentCounts[log.pitcherId] || 0) + log.pitches;
              });
              setFinalPitchCounts(currentCounts);
              setEndGameStep('score');
            }}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            rulesetOverride={tournamentRulesetOverride}
            effectiveCustomRules={tournamentCustomRules}
            isHeadCoach={isHeadCoach}
          />
        </div>

        {/* Lineup tab content */}
        {activeTab === 'lineup' && (
          <div className="px-4 pb-4 space-y-4">
            {team.settings?.entireSeasonLineup && (
              <div className="bg-surface p-4 rounded-2xl border border-hairline">
                <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-2">
                  Last Batter of Game
                </label>
                <select
                  value={lastBatterId || ''}
                  onChange={(e) => handleLastBatterChange(e.target.value)}
                  className="w-full bg-raised border border-hairline rounded-xl px-4 py-2 text-ink font-black text-sm outline-none focus:border-emerald-500 transition-colors"
                >
                  <option value="">Select Last Batter...</option>
                  {lineup.filter(p => p.position !== 'BENCH').map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}
            <LiveLineupWall
              lineup={lineup}
              inningLogs={inningLogs}
              currentInning={currentInning}
              planInnings={game.plan?.innings}
              totalInnings={totalInningsPlanned}
              pitchCounts={livePitchCounts}
              showPitches={isPlayersPitch}
            />
          </div>
        )}

        {/* Pitch Tracker tab — inline, inside the scroll area */}
        {activeTab === 'pitch_tracker' && renderTracker(() => setActiveTab('defense'))}

        {/* Action buttons + Bullpen */}
        <div className="px-4 pt-3 pb-4 safe-bottom space-y-3">
        {(inningFlowStep === 'set_defense' ? (
          <div className="grid grid-cols-3 gap-4">
            {/* Auto Lineup dropdown button */}
            <div className="relative">
              {isAutoLineupOpen && (
                <div className="fixed inset-0 z-10" onClick={() => setIsAutoLineupOpen(false)} />
              )}
              <div className="relative z-20">
                {isAutoLineupOpen ? (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-surface border border-track rounded-2xl overflow-hidden shadow-2xl z-30 max-h-[70vh] overflow-y-auto">
                    {AUTO_LINEUP_MODES.map(({ key, label, description }) => (
                      <button
                        key={key}
                        onClick={() => { setAutoLineupMode(key); setIsAutoLineupOpen(false); handleAutoLineup(key); }}
                        className={cn(
                          "w-full text-left px-3 py-2.5 transition-colors border-b border-hairline last:border-b-0",
                          autoLineupMode === key ? "bg-emerald-500/20" : "hover:bg-hairline"
                        )}
                      >
                        <div className={cn(
                          "font-black uppercase tracking-widest text-[10px]",
                          autoLineupMode === key ? "text-accent-ink" : "text-ink"
                        )}>
                          {label}
                        </div>
                        <div className="text-[9px] font-bold text-ink-2 mt-0.5 normal-case tracking-normal">
                          {description}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="w-full flex rounded-2xl overflow-hidden border border-hairline hover:border-emerald-500/50 transition-colors">
                    <button
                      onClick={() => handleAutoLineup()}
                      disabled={isGeneratingLineup}
                      className="flex-1 py-4 bg-surface flex flex-col items-center justify-center space-y-1 disabled:opacity-50"
                    >
                      <Brain className="w-5 h-5 text-emerald-500" />
                      <span className="font-black uppercase tracking-widest text-[10px]">
                        {isGeneratingLineup ? 'Generating...' : (
                          AUTO_LINEUP_MODES.find(m => m.key === autoLineupMode)?.buttonLabel ?? 'Auto Lineup'
                        )}
                      </span>
                    </button>
                    <button
                      onClick={() => setIsAutoLineupOpen(true)}
                      disabled={isGeneratingLineup}
                      className="px-2 bg-surface border-l border-hairline flex items-center justify-center disabled:opacity-50"
                    >
                      <ChevronDown className="w-3 h-3 text-ink-2" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            {isPlayersPitch && (
              <button
                onClick={() => setChangePitcherStep(currentPitcher ? 'log_pitches' : 'select_pitcher')}
                className="py-4 bg-surface border border-hairline rounded-2xl flex flex-col items-center justify-center space-y-2 hover:border-emerald-500/50 transition-colors"
              >
                <RefreshCw className="w-5 h-5 text-amber-500" />
                <span className="font-black uppercase tracking-widest text-[10px]">Change Pitcher</span>
              </button>
            )}
            <button
              onClick={handleReadyForNextInning}
              className={cn(
                "py-4 font-black rounded-2xl uppercase italic tracking-widest hover:bg-emerald-400 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]",
                isPlayersPitch ? "bg-emerald-500 text-ink" : "bg-emerald-500 text-ink col-span-2"
              )}
            >
              Ready
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={handleEndInningClick}
              className={cn(
                "py-4 bg-surface border border-hairline rounded-2xl flex flex-col items-center justify-center space-y-2 hover:border-emerald-500/50 transition-colors",
                !isPlayersPitch && "col-span-2"
              )}
            >
              <ClipboardList className="w-5 h-5 text-ink-2" />
              <span className="font-black uppercase tracking-widest text-[10px]">End Inning {currentInning}</span>
            </button>
            {isPlayersPitch && (
              <button
                onClick={() => setChangePitcherStep(currentPitcher ? 'log_pitches' : 'select_pitcher')}
                className="py-4 bg-surface border border-hairline rounded-2xl flex flex-col items-center justify-center space-y-2 hover:border-emerald-500/50 transition-colors"
              >
                <RefreshCw className="w-5 h-5 text-amber-500" />
                <span className="font-black uppercase tracking-widest text-[10px]">Change Pitcher</span>
              </button>
            )}
            <button
              onClick={() => {
                if (isPlayersPitch) {
                  isElite ? setIsPitchingIntelOpen(true) : onUpgrade?.();
                } else {
                  handleAutoLineup();
                }
              }}
              className="py-4 bg-surface border border-hairline rounded-2xl flex flex-col items-center justify-center space-y-2 hover:border-emerald-500/50 transition-colors"
            >
              {isPlayersPitch ? (
                <>
                  <Activity className={`w-5 h-5 ${isElite ? 'text-emerald-500' : 'text-ink-2'}`} />
                  <span className="font-black uppercase tracking-widest text-[10px]">
                    {isElite ? 'Pitching Intel' : '🔒 Pitching Intel'}
                  </span>
                </>
              ) : (
                <>
                  <Brain className="w-5 h-5 text-emerald-500" />
                  <span className="font-black uppercase tracking-widest text-[10px]">Auto Rotate</span>
                </>
              )}
            </button>
          </div>
        ))}

        {/* Undo last pitcher change */}
        {prevPitcherState && (
          <button
            onClick={handleUndoPitcherChange}
            className="w-full py-2 flex items-center justify-center space-x-2 text-ink-2 hover:text-gold-ink transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            <span className="text-[10px] font-black uppercase tracking-widest">Undo Pitcher Change</span>
          </button>
        )}

        {/* Bullpen */}
        <BenchAndBullpen
          benchPlayers={benchPlayers}
          bullpenPlayers={bullpenAvailable}
          bullpenUnavailable={bullpenUnavailable}
          bullpenOnRest={bullpenOnRest}
          selectedPlayerId={selectedPlayerId}
          handleBenchClick={handleBenchClick}
          handlePlayerClick={handlePlayerClick}
          isBullpenOpen={isBullpenOpen}
          setIsBullpenOpen={setIsBullpenOpen}
        />

        {/* Late arrival — always shown when there are absent players */}
        {isHeadCoach && absentPlayers.length > 0 && (
          <button
            onClick={() => setIsRosterManagerOpen(true)}
            className="w-full py-3 flex items-center justify-center space-x-2 text-ink-2 hover:text-accent-ink transition-colors border border-dashed border-hairline hover:border-emerald-500/40 rounded-2xl"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span className="text-[10px] font-black uppercase tracking-widest">Add Late Arrival</span>
            <span className="text-[9px] font-bold text-ink-2">({absentPlayers.length})</span>
          </button>
        )}
        </div>{/* end action buttons */}
      </div>{/* end scrollable area */}

      {/* Add / Remove Player — one roster list, each row toggling in or out of
          today's game. Opened from the game menu (top left). */}
      <AnimatePresence>
        {isRosterManagerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink/60 flex items-end justify-center backdrop-blur-sm"
            onClick={() => setIsRosterManagerOpen(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="w-full max-w-lg bg-surface rounded-t-3xl p-6 pb-6 safe-bottom space-y-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-black uppercase text-sm text-ink">Add / Remove Player</h3>
                  <p className="text-[10px] font-bold text-ink-2 uppercase tracking-widest mt-0.5">
                    {lineup.length} of {players.length} available
                  </p>
                </div>
                <button
                  onClick={() => setIsRosterManagerOpen(false)}
                  className="p-2 rounded-xl hover:bg-hairline transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-ink-2" />
                </button>
              </div>

              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {players.map((player: any) => {
                  const entry = lineup.find((l: any) => l.id === player.id);
                  const isIn = !!entry;
                  const onMound = isIn && entry.position === 'P' && isPlayersPitch;
                  const where = !isIn
                    ? (lateArrivals.includes(player.id) ? 'Arriving late' : 'Not in this game')
                    : entry.position === 'BENCH' || entry.position === 'EH'
                      ? 'On the bench'
                      : `Playing ${entry.position}`;
                  return (
                    <div
                      key={player.id}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-colors",
                        isIn ? "bg-raised border-hairline" : "bg-raised/50 border-hairline/60"
                      )}
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center font-black text-[10px] shrink-0",
                          isIn ? "bg-hairline text-ink" : "bg-surface text-ink-2"
                        )}>
                          #{player.number || '?'}
                        </div>
                        <div className="text-left min-w-0">
                          <span className={cn(
                            "font-black text-sm block truncate",
                            isIn ? "text-ink" : "text-ink-2"
                          )}>
                            {player.name}
                          </span>
                          <span className={cn(
                            "text-[9px] font-bold",
                            onMound ? "text-amber-500" : isIn ? "text-ink-2" : "text-ink-2"
                          )}>
                            {where}
                          </span>
                        </div>
                      </div>

                      {/* The toggle: on = in today's game, off = out. */}
                      <button
                        role="switch"
                        aria-checked={isIn}
                        aria-label={`${isIn ? 'Remove' : 'Add'} ${player.name}`}
                        onClick={() => {
                          if (isIn) handleRemovePlayer(player);
                          else confirmAddLatePlayer(false, player);
                        }}
                        className={cn(
                          "relative w-11 h-6 rounded-full shrink-0 ml-3 transition-colors",
                          isIn ? "bg-emerald-500" : "bg-track"
                        )}
                      >
                        <span className={cn(
                          "absolute top-0.5 w-5 h-5 rounded-full bg-surface shadow-sm transition-all",
                          isIn ? "left-[22px]" : "left-0.5"
                        )} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Late Player Modal */}
      <AnimatePresence>
        {isLatePlayerModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink/60 flex items-end justify-center"
            onClick={() => setIsLatePlayerModalOpen(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="w-full max-w-lg bg-surface rounded-t-3xl p-6 pb-6 safe-bottom space-y-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-black uppercase text-sm text-ink">Add to Bench</h3>
                  <p className="text-[10px] font-bold text-ink-2 uppercase tracking-widest mt-0.5">Player goes straight to the bench</p>
                </div>
                <button onClick={() => setIsLatePlayerModalOpen(false)} className="p-2 rounded-xl hover:bg-hairline transition-colors">
                  <X className="w-4 h-4 text-ink-2" />
                </button>
              </div>
              <div className="space-y-4 max-h-72 overflow-y-auto">
                {absentPlayers.some(p => lateArrivals.includes(p.id)) && (
                  <div className="space-y-2">
                    <p className="text-[9px] font-black uppercase tracking-widest text-amber-500">Arriving Late</p>
                    {absentPlayers.filter(p => lateArrivals.includes(p.id)).map((player: any) => (
                      <button
                        key={player.id}
                        onClick={() => handleAddLatePlayer(player)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-raised border border-amber-500/30 rounded-2xl hover:border-amber-500/60 hover:bg-hairline/50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-hairline rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                            #{player.number || '?'}
                          </div>
                          <div className="text-left">
                            <span className="font-black text-sm block">{player.name}</span>
                            <span className="text-[9px] text-amber-500 font-bold">Expected</span>
                          </div>
                        </div>
                        <UserPlus className="w-4 h-4 text-emerald-500" />
                      </button>
                    ))}
                  </div>
                )}
                {absentPlayers.some(p => !lateArrivals.includes(p.id)) && (
                  <div className="space-y-2">
                    {absentPlayers.some(p => lateArrivals.includes(p.id)) && (
                      <p className="text-[9px] font-black uppercase tracking-widest text-ink-2">Not in Lineup</p>
                    )}
                    {absentPlayers.filter(p => !lateArrivals.includes(p.id)).map((player: any) => (
                      <button
                        key={player.id}
                        onClick={() => handleAddLatePlayer(player)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-raised border border-hairline rounded-2xl hover:border-emerald-500/50 hover:bg-hairline/50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-hairline rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                            #{player.number || '?'}
                          </div>
                          <span className="font-black text-sm">{player.name}</span>
                        </div>
                        <UserPlus className="w-4 h-4 text-emerald-500" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inning Flow Modals */}
      <AnimatePresence>
        <InningFlowModal
          key="inning-flow"
          inningFlowStep={inningFlowStep}
          setInningFlowStep={setInningFlowStep}
          currentInning={currentInning}
          currentLoggingPitcherId={currentLoggingPitcherId}
          setCurrentLoggingPitcherId={setCurrentLoggingPitcherId}
          inningPitches={inningPitches}
          setInningPitches={setInningPitches}
          handleSavePitches={handleSavePitches}
          handleAddAnotherPitcher={handleAddAnotherPitcher}
          lineup={lineup}
          currentPitcher={currentPitcher}
          handlePitcherContinue={handlePitcherContinue}
          plannedNextPitcher={plannedPitcherNextInning}
          onBringInPlannedPitcher={handleBringInPlannedPitcher}
          fairnessFindings={fairnessFindings}
          onAutoBalance={handleAutoBalanceFromFairness}
          isAutoBalancing={isGeneratingLineup}
        />

        <EndGameModal
          key="end-game"
          endGameStep={endGameStep}
          setEndGameStep={setEndGameStep}
          awayScore={awayScore}
          setAwayScore={setAwayScore}
          homeScore={homeScore}
          setHomeScore={setHomeScore}
          finalPitchCounts={finalPitchCounts}
          setFinalPitchCounts={setFinalPitchCounts}
          isFinishingGame={isFinishingGame}
          setIsFinishingGame={setIsFinishingGame}
          handleFinishGame={handleFinishGame}
          saveError={finishGameError}
          game={game}
          team={team}
          players={players}
          catcherEvents={catcherEvents}
          trackGameBalls={team.settings?.trackGameBalls ?? false}
          selectedGameBallIds={selectedGameBallIds}
          setSelectedGameBallIds={setSelectedGameBallIds}
        />

        {isPitchingIntelOpen && (
          <PitchingIntelPanel
            key="pitching-intel"
            lineup={lineup}
            pitchCounts={livePitchCounts}
            pitchedThisGame={pitchedThisGame}
            inningLogs={inningLogs}
            team={team}
            players={players}
            teamId={teamId}
            gameId={game.id}
            games={games}
            tournamentId={game.tournamentId}
            tournament={game.tournamentId ? tournaments.find((t: any) => t.id === game.tournamentId) : undefined}
            gameDate={game.date}
            rulesetOverride={tournamentRulesetOverride}
            enforceGlobalRest={(() => {
              const tournament = game.tournamentId
                ? tournaments.find((t: any) => t.id === game.tournamentId)
                : null;
              return tournament?.enforceGlobalRest ?? team.settings?.enforceGlobalRest ?? true;
            })()}
            onSubstitution={async (newLineup, inningPitches) => {
              snapshotPitcherState();

              // Track outgoing pitcher and new pitcher in pitchedThisGame
              const incomingPitcher = newLineup.find((p: any) => p.position === 'P');
              const newPitchedThisGame = [...new Set([
                ...pitchedThisGame,
                ...(currentPitcher ? [currentPitcher.id] : []),
                ...(incomingPitcher ? [incomingPitcher.id] : []),
              ])];

              // Apply any pitch count from the log step
              const newCounts = { ...pitchCounts };
              if (inningPitches !== undefined && currentPitcher) {
                newCounts[currentPitcher.id] = (newCounts[currentPitcher.id] || 0) + inningPitches;
              }

              setLineup(newLineup);
              setPitchedThisGame(newPitchedThisGame);
              setPitchCounts(newCounts);
              setIsPitchingIntelOpen(false);
              try {
                await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
                  lineup: deepClean(newLineup),
                  pitchedThisGame: newPitchedThisGame,
                  pitchCounts: newCounts
                }, { merge: true });
              } catch (error) {
                console.error("Failed to update lineup", error);
              }
            }}
            onClose={() => setIsPitchingIntelOpen(false)}
          />
        )}

        {showCelebration && (
          <TournamentCelebration
            key="celebration"
            game={game}
            result={gameResult}
            teamId={teamId}
            teamName={team.name}
            onClose={() => {
              setShowCelebration(false);
              setShowAdvisor(true);
            }}
          />
        )}

        {showAdvisor && (
          <BetweenGamesAdvisor
            key="between-games"
            game={game}
            team={team}
            players={players}
            games={games}
            tournament={game.tournamentId ? tournaments.find((t: any) => t.id === game.tournamentId) : undefined}
            onClose={onClose}
          />
        )}

        {showDiscardConfirm && (
          <motion.div
            key="discard-confirm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-ink/60 flex items-end justify-center pb-8 safe-bottom px-4"
            onClick={() => setShowDiscardConfirm(false)}
          >
            <motion.div
              initial={{ y: 40, scale: 0.96 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 40, scale: 0.96 }}
              className="w-full max-w-sm bg-surface border border-track rounded-3xl p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <p className="text-xs font-black uppercase tracking-widest text-gold-ink mb-1">Discard Game?</p>
              <p className="text-sm text-ink-2 leading-relaxed mb-6">
                This will clear all innings, pitch counts, and tracker data recorded so far. The game stays on your schedule as unplayed. This cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDiscardConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl bg-hairline text-xs font-black uppercase tracking-widest text-ink hover:bg-track transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDiscardGame}
                  className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/40 text-xs font-black uppercase tracking-widest text-danger hover:bg-red-500/30 transition-colors"
                >
                  Discard Game
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>

      {/* Change Pitcher Modal */}
      <AnimatePresence>
        {changePitcherStep && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-md bg-surface rounded-3xl border border-hairline shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-hairline flex items-center justify-between">
                <h3 className="text-lg font-black italic uppercase tracking-tight">Change Pitcher</h3>
                <button onClick={() => { setChangePitcherStep(null); setShowMorePitchers(false); }} className="p-2 hover:bg-hairline rounded-full transition-colors">
                  <X className="w-5 h-5 text-ink-2" />
                </button>
              </div>

              <div className="p-6">
                {changePitcherStep === 'log_pitches' ? (
                  <div className="space-y-6">
                    <div className="p-4 bg-raised rounded-2xl border border-hairline">
                      <div className="text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-4">Current Pitcher Stats</div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 font-black">
                            #{currentPitcher?.number}
                          </div>
                          <div>
                            <div className="text-sm font-black uppercase">{currentPitcher?.name}</div>
                            <div className="text-[10px] font-bold text-ink-2 uppercase tracking-widest">Inning {currentInning}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-black italic text-amber-500">{currentPitchCount}</div>
                          <div className="text-[8px] font-bold text-ink-2 uppercase tracking-widest">Total Pitches</div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-2">Pitches Thrown this Inning</label>
                        <input
                          type="number"
                          value={changePitcherInningPitches}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            if (!val) { setChangePitcherInningPitches(''); return; }
                            const num = parseInt(val, 10);
                            setChangePitcherInningPitches(String(Math.min(Math.max(num, 0), 200)));
                          }}
                          placeholder="0"
                          className="w-full bg-raised border border-hairline rounded-xl px-4 py-3 text-ink font-black text-xl outline-none focus:border-emerald-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-2">Outs at time of change</label>
                        <div className="grid grid-cols-3 gap-2">
                          {['0', '1', '2'].map(out => (
                            <button
                              key={out}
                              onClick={() => setChangePitcherOuts(out)}
                              className={cn(
                                "py-3 rounded-xl font-black text-sm transition-all",
                                changePitcherOuts === out ? "bg-emerald-500 text-ink" : "bg-raised text-ink-2 border border-hairline"
                              )}
                            >
                              {out} Outs
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <button 
                      onClick={handleLogChangePitcher}
                      disabled={!changePitcherInningPitches}
                      className="w-full py-4 bg-emerald-500 text-ink font-black rounded-2xl uppercase italic tracking-widest hover:bg-emerald-400 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)] disabled:opacity-50"
                    >
                      Next: Select New Pitcher
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      {nextPlannedArm && (
                        <p className="text-sm font-semibold text-accent-ink mb-3">
                          {nextPlannedArm.name.split(' ')[0]} is slotted next in the bullpen.
                        </p>
                      )}
                      <div className="text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-3">Available Bullpen Arms</div>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                        {changePitcherArms.map(p => (
                          <button
                            key={p.id}
                            onClick={() => handleSelectNewPitcher(p.id)}
                            className={`w-full p-4 bg-raised border rounded-2xl flex items-center justify-between hover:border-emerald-500/50 transition-all group ${
                              p.id === nextPlannedArm?.id ? 'border-accent ring-2 ring-accent/40' : 'border-hairline'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-full bg-surface border border-hairline flex items-center justify-center text-ink font-black group-hover:border-emerald-500/30">
                                #{p.number}
                              </div>
                              <div className="text-left">
                                <div className="text-sm font-black uppercase">{p.name}</div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-[8px] font-bold text-ink-2 uppercase tracking-widest">{p.position}</span>
                                  {p.attributes?.pitching?.velocity > 0 && (
                                    <span className="text-[8px] font-bold text-emerald-500 uppercase tracking-widest">Velocity: {p.attributes.pitching.velocity}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-ink-mute group-hover:text-emerald-500 transition-colors" />
                          </button>
                        ))}
                        {changePitcherArms.length === 0 && (
                          <p className="text-xs text-ink-2 italic px-1">No available arms</p>
                        )}
                      </div>
                    </div>
                    {bullpenOnRest.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold text-amber-500/70 uppercase tracking-widest mb-3">Mandatory Rest</div>
                        <div className="space-y-2 pr-2">
                          {bullpenOnRest.map(({ player, until }) => (
                            <button
                              key={player.id}
                              onClick={() => handleSelectNewPitcher(player.id)}
                              className="w-full p-4 bg-amber-500/5 border border-amber-500/30 rounded-2xl flex items-center justify-between hover:border-amber-400/60 transition-all group"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-surface border border-amber-500/30 flex items-center justify-center text-ink font-black">
                                  #{player.number}
                                </div>
                                <div className="text-left">
                                  <div className="text-sm font-black uppercase">{player.name}</div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-[8px] font-bold text-ink-2 uppercase tracking-widest">{player.position}</span>
                                    <span className="text-[8px] font-bold text-gold-ink uppercase tracking-widest">Rest until {until}</span>
                                  </div>
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* See More Players — bring in someone who isn't a tagged pitcher.
                        Selecting one tags them as a pitcher going forward. */}
                    {moreBullpenPlayers.length > 0 && (
                      <div>
                        {!showMorePitchers ? (
                          <button
                            onClick={() => setShowMorePitchers(true)}
                            className="w-full py-3 rounded-2xl border border-dashed border-track text-[11px] font-bold uppercase tracking-widest text-ink-2 hover:text-ink hover:border-ink-2 transition-colors"
                          >
                            See More Players ({moreBullpenPlayers.length})
                          </button>
                        ) : (
                          <>
                            <div className="text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-1">Rest of Roster</div>
                            <p className="text-[10px] text-ink-2 mb-3 leading-relaxed">Not tagged as pitchers. Picking one sends them to the mound and tags them as a pitcher for next time.</p>
                            <div className="space-y-2 max-h-[240px] overflow-y-auto pr-2">
                              {moreBullpenPlayers.map((p: any) => (
                                <button
                                  key={p.id}
                                  onClick={() => handleSelectFromMore(p.id)}
                                  className="w-full p-4 bg-raised border border-hairline rounded-2xl flex items-center justify-between hover:border-emerald-500/50 transition-all group"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 rounded-full bg-surface border border-hairline flex items-center justify-center text-ink font-black group-hover:border-emerald-500/30">
                                      #{p.number}
                                    </div>
                                    <div className="text-left">
                                      <div className="text-sm font-black uppercase">{p.name}</div>
                                      <span className="text-[8px] font-bold text-ink-2 uppercase tracking-widest">{p.position}</span>
                                    </div>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-ink-mute group-hover:text-emerald-500 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fill Empty Position Modal */}
      <AnimatePresence>
        <FillEmptyPositionModal
          emptyPositionToFill={emptyPositionToFill}
          setEmptyPositionToFill={setEmptyPositionToFill}
          benchPlayers={benchPlayers}
          handleFillEmptyPosition={handleFillEmptyPosition}
        />
      </AnimatePresence>

      {/* In-app alert modal (replaces native alert() for mobile) */}
      <AnimatePresence>
        {alertModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-end justify-center pb-8 safe-bottom px-4"
            onClick={() => setAlertModal(null)}
          >
            <motion.div
              initial={{ y: 40, scale: 0.96 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 40, scale: 0.96 }}
              className="w-full max-w-sm bg-surface border border-track rounded-3xl p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <p className="text-xs font-black uppercase tracking-widest text-gold-ink mb-1">{alertModal.title}</p>
              <p className="text-sm text-ink-2 leading-relaxed mb-4">{alertModal.message}</p>
              <button
                onClick={() => setAlertModal(null)}
                className="w-full py-2.5 rounded-xl bg-hairline text-xs font-black uppercase tracking-widest text-ink hover:bg-track transition-colors"
              >
                {alertModal.buttonLabel || 'Got it'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm modal — used for the mandatory-rest override before a pitcher swap */}
      <AnimatePresence>
        {confirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-end justify-center pb-8 safe-bottom px-4 bg-ink/40"
            onClick={() => setConfirmModal(null)}
          >
            <motion.div
              initial={{ y: 40, scale: 0.96 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 40, scale: 0.96 }}
              className="w-full max-w-sm bg-surface border border-track rounded-3xl p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <p className="text-xs font-black uppercase tracking-widest text-gold-ink mb-1">{confirmModal.title}</p>
              <p className="text-sm text-ink-2 leading-relaxed mb-4 whitespace-pre-line">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="flex-1 py-2.5 rounded-xl bg-hairline text-xs font-black uppercase tracking-widest text-ink hover:bg-track transition-colors"
                >
                  {confirmModal.cancelLabel || 'Cancel'}
                </button>
                <button
                  onClick={() => confirmModal.onConfirm()}
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 text-xs font-black uppercase tracking-widest text-ink hover:bg-amber-400 transition-colors"
                >
                  {confirmModal.confirmLabel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      </div>
      {isWide && (
        <div className="relative w-1/2 min-w-0 flex flex-col border-l border-hairline bg-canvas">
          <SidePane
            view={sideView}
            onView={setSideView}
            isPlayersPitch={isPlayersPitch}
            currentInning={currentInning}
            totalInnings={totalInningsPlanned}
            pager={pagerState}
            nextPlannedArm={nextPlannedArm}
            plannedPitcherNextInning={plannedPitcherNextInning}
            bullpenAvailable={bullpenAvailable}
            bullpenOnRest={bullpenOnRest}
            bullpenUnavailable={bullpenUnavailable}
            lineup={lineup}
            inningLogs={inningLogs}
            planInnings={game.plan?.innings}
            settings={team.settings}
            rosterNames={rosterNames}
            removedPlayerIds={removedPlayerIds}
            trackerNode={renderTracker(() => setSideView('next'))}
          />
        </div>
      )}
      </div>
    </motion.div>
  );
};

export { LiveGameEngine };